import { Alert, Button, Input, InputNumber, Message, Modal, Popconfirm, Switch, Tag } from "@arco-design/web-react";
import { ArrowLeft, ArrowUp, Calculator, CheckCircle2, ChevronDown, Copy, Download, Eye, PencilLine, Plus, Save, Users } from "lucide-react";
import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import { isHttpClientError } from "../../../lib/http/errors";
import { useBusinessPageTitle } from "../../../lib/navigation/useBusinessPageTitle";
import { useUnsavedChangesGuard } from "../../../shared/navigation/useUnsavedChangesGuard";
import { HorizontalScrollRegion } from "../../../shared/ui/HorizontalScrollRegion";
import { formatFeeAdjustmentAmount, type FeeAdjustmentRecord } from "../../fee-adjustment";
import { buildFeeAdjustmentQuoteSummary } from "../service/feeAdjustmentQuoteSummary";
import { useCustomerQuoteFeeAdjustments } from "../runtime/useCustomerQuoteFeeAdjustments";
import { useCustomerQuoteRuntime } from "../runtime/useCustomerQuoteRuntime";
import {
  applyCustomerQuotePricingGroupMarkup,
  buildCustomerQuotePricingGroups,
  buildCustomerQuoteRoundedPresentation,
  formatCustomerQuoteWholeYuan,
  calculateCustomerQuoteRoleSummary,
  calculateCustomerQuoteHeaderMetrics,
  calculateCustomerQuoteAgentPerStudent,
  calculateCustomerQuoteAgentPresentation,
  addCustomerQuoteAgentPerStudent,
  compareCustomerQuoteMoney,
  customerQuoteMarkupRatePercent,
  customerQuoteBusScope,
  customerQuotePricingGroupBaseMarkup,
  customerQuoteUnitMarkup,
  exceedsCustomerQuoteBusAutoMarkupCap,
  formatCustomerQuoteMarginRatePercent,
  buildCustomerQuotePointDisplayRuns,
  customerQuoteDetailQuantityHeading,
  formatCustomerQuoteLineQuantity,
  formatCustomerQuoteMoney,
  formatCustomerQuoteCompactDecimal,
  groupCustomerQuoteLines,
  hasHighCustomerQuoteStayMarkup,
  canToggleCustomerQuoteAgentCollection,
  normalizeCustomerQuoteMoney,
  validateCustomerQuoteDraft,
} from "../service/draftService";
import type {
  CustomerQuoteAllocationResult,
  CustomerQuoteLine,
  CustomerQuotePricingGroup,
  CustomerQuotePreview,
  CustomerQuoteSnapshot,
  CustomerQuoteSourceChange,
  CustomerQuoteStandardExportField,
  CustomerQuoteStandardFinancialSummary,
  CustomerQuoteTemplateType,
} from "../types";
import { buildCustomerQuoteFeePreviewGroups, describeCustomerQuoteFeeTotal } from "../service/feePreviewService";
import { CustomerQuoteSourceChanges } from "./CustomerQuoteSourceChanges";
import { CustomerQuoteBudgetReturnAction } from "./CustomerQuoteBudgetReturnAction";
import "./customerQuoteEditor.css";
import "./customerQuoteFeePreview.css";
import "./customerQuoteSplit.css";

const templateLabels: Record<CustomerQuoteTemplateType, string> = {
  transparent: "透明报价",
  standard: "标准报价",
  package: "打包报价",
};

const standardCustomerQuoteNote = "本报价已按团队整体服务方案核算，具体费用以最终确认方案为准。";

const defaultStandardFieldVisibility: Record<CustomerQuoteStandardExportField, boolean> = {
  quoteAmount: false,
  roundedAmount: false,
  unitPrice: true,
};

const includedItemSuggestions = ["住宿", "餐饮", "门票", "当地交通", "导游服务"];
const excludedItemSuggestions = ["往返大交通", "个人消费", "单房差", "行程外费用"];

const displayOptions = [
  { value: "show", label: "显示" },
  { value: "merge", label: "合并" },
] as const;

type CustomerQuoteDisplayMenuProps = {
  groupName: string;
  value: CustomerQuoteLine["displayMode"] | "mixed";
  disabled: boolean;
  disabledReason?: string;
  options?: readonly (typeof displayOptions)[number][];
  onChange: (value: CustomerQuoteLine["displayMode"]) => void;
};

/** 对客展示模式沿用分组变更规则，以并排按钮直接切换。 */
function CustomerQuoteDisplayMenu({ groupName, value, disabled, disabledReason, options = displayOptions, onChange }: CustomerQuoteDisplayMenuProps) {
  return <Button.Group className="customer-quote-display-switch" role="group" aria-label={`${groupName}展示方式`}>
    {options.map((option) => <Button key={option.value} size="mini" type={value === option.value ? "primary" : "secondary"}
      aria-label={`${groupName}${option.label}`} aria-pressed={value === option.value} disabled={disabled} title={disabledReason}
      onClick={() => { if (option.value !== value) onChange(option.value); }}>{option.label}</Button>)}
  </Button.Group>;
}

const feeCategoryRuleLabels: Record<CustomerQuoteLine["feeCategory"], string> = {
  big_transport: "代收代付不加价、不取整；合同内可手动加价，默认 0，不参与 16% 自动报价。",
  bus: "自动加价不超过成本的 30%。",
  stay: "仅学生人晚住宿自动加价，每人晚最高 ¥10；其他角色默认加价 ¥0，可分别手动调整。非人晚住宿保持成本，目标利润转入服务保障。",
  meal: "可手动加价，不参与 16% 自动报价调配。",
  ticket: "代收代付不加价；合同内可手动加价，默认 0，不参与 16% 自动摊销。",
  enterprise: "代收代付不加价；合同内可手动加价，默认 0，不参与 16% 自动摊销。",
  intangible: "代收代付不加价；合同内可手动加价，默认 0，不参与 16% 自动摊销。",
  guide: "仅导服费默认显示，其他费用默认合并，可由销售切换。可手动加价，不参与 16% 自动报价调配。",
  service_fee: "已有服务保障承接剩余加价；不足时新增零成本保障费。",
  tax: "税费固定单列展示，不参与主动加价。",
  misc: "可人工加价；仅可在“其他”内显示或合并，不并入服务保障。",
  teacher: "老师费用按是否自付决定单列或摊入学生。",
  manual_adjustment: "仅用于有原因、有留痕的报价调整。",
};

const statusLabels: Record<string, string> = {
  draft: "草稿",
  finalized: "已定稿",
  superseded: "历史版本",
};

const expectedTargetMarkupRate = 0.16;

const moneyInputErrorMessage = "金额无效或超出范围，已恢复为最近有效值。";
const markupInputErrorMessage = "加价金额无效或会使报价为负，已恢复为最近有效值。";

/** 按规则版本返回可解释的费用分类提示，历史快照不得伪装为现行口径。 */
function customerQuoteFeeCategoryRuleLabel(
  feeCategory: CustomerQuoteLine["feeCategory"],
  pricingRulesVersion: number | undefined,
): string {
  if ((pricingRulesVersion ?? 1) < 3 && feeCategory === "stay") {
    return "旧规则报价沿用当时住宿加价规则和快照金额；新建或重新自动报价后仅学生人晚住宿按每人晚 ¥10 封顶，其他角色默认加价 ¥0。";
  }
  if (
    (pricingRulesVersion ?? 1) < 2
    && ["ticket", "enterprise", "intangible", "big_transport"].includes(feeCategory)
  ) {
    return "历史报价沿用当时快照金额；现行规则可手动加价，默认 0。";
  }
  if (
    (pricingRulesVersion ?? 1) >= 3
    && feeCategory === "meal"
  ) {
    return `${feeCategoryRuleLabels[feeCategory]} 当前规则要求实际费用独立展示，不可合并。`;
  }
  return feeCategoryRuleLabels[feeCategory];
}

/** 判断当前规则中必须独立向客户表达的实际费用。 */
function isCustomerQuoteActualCostDisplayLocked(
  pricingRulesVersion: number | undefined,
  feeCategory: CustomerQuoteLine["feeCategory"],
): boolean {
  return (pricingRulesVersion ?? 1) >= 3
    && feeCategory === "meal";
}

/** 门票/校企/非遗明细需要单独展示考察点列。 */
function showsCustomerQuotePointColumn(
  feeCategory: CustomerQuoteLine["feeCategory"],
): boolean {
  return feeCategory === "ticket"
    || feeCategory === "enterprise"
    || feeCategory === "intangible";
}

/** 加价输入无障碍名称：同名门票带考察点，避免多点共用同一标签。 */
function customerQuoteMarkupAriaLabel(
  pricingGroup: CustomerQuotePricingGroup,
  line: CustomerQuoteLine,
): string {
  const subject = pricingGroup.pointLabel
    ? `${pricingGroup.name}·${pricingGroup.pointLabel}`
    : pricingGroup.lines.length > 1
      ? pricingGroup.name
      : line.customerName;
  return pricingGroup.lines.length > 1
    ? `${subject}统一每单位加价`
    : `${subject}每单位加价`;
}

/** 把快照角色转换为报价明细中的中文标签。 */
function customerQuoteRoleLabel(line: CustomerQuoteLine): string {
  const role = (line.travelerRole ?? "").trim().toLowerCase();
  if (role === "student" || role === "学生") return "学生";
  if (["teacher", "老师", "教师", "院长"].includes(role)) return "老师";
  if (role === "guide" || role === "导游" || role === "领队") return "导游";
  if (role === "driver" || role === "司机") return "司机";
  return line.travelerRole?.trim() || "全员";
}

const feeCategoryLabels: Record<CustomerQuoteLine["feeCategory"], string> = {
  big_transport: "大交通",
  bus: "大巴车",
  stay: "住宿",
  meal: "餐饮",
  ticket: "门票",
  enterprise: "企业参访",
  intangible: "研学活动",
  guide: "导游",
  service_fee: "服务保障",
  tax: "税费",
  misc: "其他",
  teacher: "老师费用",
  manual_adjustment: "报价调整",
};

/** 精确计算并格式化两项核价成本的差额。 */
function formatSourceCostDelta(oldCost: string | null, newCost: string | null): string {
  const oldValue = normalizeCustomerQuoteMoney(oldCost ?? "0") ?? "0.00";
  const newValue = normalizeCustomerQuoteMoney(newCost ?? "0") ?? "0.00";
  const toCents = (value: string) => {
    const negative = value.startsWith("-");
    const [integer, decimal = "00"] = value.replace(/^[+-]/, "").split(".");
    const cents = BigInt(integer) * 100n + BigInt(decimal.padEnd(2, "0").slice(0, 2));
    return negative ? -cents : cents;
  };
  const delta = toCents(newValue) - toCents(oldValue);
  const absolute = delta < 0n ? -delta : delta;
  const amount = `${absolute / 100n}.${String(absolute % 100n).padStart(2, "0")}`;
  return `${delta > 0n ? "+" : delta < 0n ? "-" : ""}${formatCustomerQuoteMoney(amount)}`;
}

type SourceChangeWarningProps = {
  change: CustomerQuoteSourceChange;
  confirming: boolean;
  canConfirm: boolean;
  blockedReason?: string;
  isDirty: boolean;
  onConfirm: (changeId: string, discardLocalChanges?: boolean) => Promise<unknown>;
};

/** 在来源变化所在位置展示成本范围，并承接单项确认。 */
function SourceChangeWarning({ change }: SourceChangeWarningProps) {
  return <div className="customer-quote-source-change" id={`customer-quote-change-${change.changeId}`} tabIndex={-1}>
    <Tag color={change.changeType === "deleted" ? "red" : "orange"}>{change.changeType === "deleted" ? "删除待确认" : "修改待确认"}</Tag>
    <strong>{change.itemName}</strong>
    <Button type="text" size="mini" onClick={() => {
      const target = document.getElementById(`customer-quote-pending-change-${change.changeId}`);
      target?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      target?.focus({ preventScroll: true });
    }}>到待确认区核对并设置加价</Button>
  </div>;
}

/** 格式化当前成本加价率相对 16% 目标的百分点差。 */
function markupPointDifference(currentMarkupRate: string): string {
  const current = Number(currentMarkupRate);
  const points = (Number.isFinite(current) ? current - expectedTargetMarkupRate : 0) * 100;
  if (Math.abs(points) < 0.005) return "0.00";
  return `${points > 0 ? "+" : ""}${points.toFixed(2)}`;
}

/** 首屏失败使用稳定中文，避免把网络库或状态码原文直接暴露给用户。 */
function customerQuoteLoadErrorText(value: unknown): string {
  if (isHttpClientError(value) && value.code === "CUSTOMER_QUOTE_INVALID_SNAPSHOT") {
    return "报价历史数据校验失败，当前版本无法打开；请返回其他版本或联系管理员处理。";
  }
  if (isHttpClientError(value) && value.code === "CUSTOMER_QUOTE_NOT_FOUND") {
    return "报价不存在或已删除，无法打开当前链接；请返回课程线路重新选择。";
  }
  return "对客报价加载失败，请稍后重试。";
}

/** 仅为可恢复的首屏加载错误提供原地重试。 */
function customerQuoteLoadErrorCanRetry(value: unknown): boolean {
  return !isHttpClientError(value) || value.retriable;
}

const customerQuoteConflictMessages: Record<string, string> = {
  CUSTOMER_QUOTE_READONLY: "当前报价已定稿；如需新的方案，请复制核价版本后重新报价。",
  CUSTOMER_QUOTE_SOURCE_NOT_FINALIZED: "来源核价正在修改，请等待核价重新定稿后重试。",
  CUSTOMER_QUOTE_SOURCE_CHANGED: "来源核价已变化，请逐项确认最新核价变化后继续编辑报价。",
  CUSTOMER_QUOTE_SOURCE_CHANGES_PENDING: "来源核价已变化，请逐项确认最新核价变化后继续编辑报价。",
  CUSTOMER_QUOTE_SOURCE_CHANGE_STALE: "报价数据已发生变化，请刷新后重试。",
  CUSTOMER_QUOTE_CONCURRENT_UPDATE: "报价数据已发生变化，请刷新后重试。",
};

/** 将报价接口异常收敛为稳定中文，避免把 Axios 原始消息直接暴露给用户。 */
function customerQuoteActionErrorText(
  value: unknown,
  fallback: string,
  sourceBlockReason?: string | null,
  sourceState?: string | null,
): string {
  if (isHttpClientError(value)) {
    if (value.code === "CUSTOMER_QUOTE_TARGET_INVALID" && /[\u3400-\u9fff]/u.test(value.message)) return value.message;
    if (value.statusCode === 503 && sourceState === "unavailable" && sourceBlockReason) {
      return sourceBlockReason;
    }
    if (value.statusCode === 403) return "无权限执行当前报价操作，请联系管理员开通对应权限。";
    if (value.statusCode === 409) {
      return customerQuoteConflictMessages[value.code ?? ""]
        ?? "当前操作发生冲突，请刷新后重试。";
    }
    return fallback;
  }
  if (value instanceof Error && /[\u3400-\u9fff]/u.test(value.message)) return value.message;
  return fallback;
}

/** 优先使用 Clipboard API，不可用时兼容非安全上下文浏览器。 */
async function writeClipboardText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.readOnly = true;
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  try {
    textarea.select();
    if (!document.execCommand?.("copy")) {
      throw new Error("当前浏览器不支持自动复制");
    }
  } finally {
    textarea.remove();
  }
}

/** 判断原始十进制文本是否具有非零负值，避免两位量化把负小数吞成零。 */
function isNegativeDecimalInput(value: string): boolean {
  const match = /^-(\d+)(?:\.(\d*))?$/.exec(value.trim());
  return Boolean(match && /[1-9]/.test(`${match[1]}${match[2] ?? ""}`));
}

/** 归一化报价金额，并在配置下限时拒绝越界值。 */
function normalizeQuoteAmount(value: string, min?: number): string | null {
  if (min !== undefined && min >= 0 && isNegativeDecimalInput(value)) return null;
  const normalized = normalizeCustomerQuoteMoney(value);
  if (normalized === null) return null;
  return min !== undefined && compareCustomerQuoteMoney(normalized, String(min)) < 0
    ? null
    : normalized;
}

/** 判断合法十进制数量是否为零，不经过 JavaScript Number。 */
function isZeroQuantity(value: string): boolean {
  return /^[+-]?0+(?:\.0*)?$/.test(value.trim());
}

/** 返回输入字段错误提示的稳定关联 ID。 */
function customerQuoteFieldErrorId(field: string): string {
  return `customer-quote-${field}-error`;
}

/** 合并存在的描述 ID，供输入同时关联帮助与错误文本。 */
function joinAriaDescriptions(
  ...ids: Array<string | false | null | undefined>
): string | undefined {
  const value = ids.filter(Boolean).join(" ");
  return value || undefined;
}

/** 在字段后显示与输入关联的可访问错误提示。 */
function CustomerQuoteFieldError({
  field,
  message,
}: {
  field: string;
  message?: string;
}) {
  if (!message) return null;
  return (
    <small
      id={customerQuoteFieldErrorId(field)}
      className="customer-quote__field-error"
      role="alert"
    >
      {message}
    </small>
  );
}

/** 为自动计算金额提供可核对的数量、核价单价和加价公式。 */
function calculatedAmountFormula(line: CustomerQuoteLine, markup: string): string {
  return `${formatCustomerQuoteCompactDecimal(line.quantity)} × (${formatCustomerQuoteMoney(line.costUnitPrice, true)} + ${formatCustomerQuoteMoney(markup, true)}) = ${formatCustomerQuoteMoney(line.quoteAmount, true)}`;
}

/** 对客报价编辑器属性。 */
export type CustomerQuoteEditorFeaturePageProps = {
  customerQuoteId: string;
  feeAdjustmentContext?: {
    orderId: string;
    rosterId: string;
  };
};

type PendingTemplateSwitch = {
  customerQuoteId: string;
  actionRequestId: number;
  requestedTemplateType: CustomerQuoteTemplateType;
  successfulTemplateType: CustomerQuoteTemplateType;
  rollbackPersistedTemplate: () => void;
};

/** 首屏加载骨架，保持工作台尺寸稳定。 */
function LoadingWorkbench() {
  return (
    <div className="customer-quote customer-quote--loading" role="status" aria-label="对客报价加载中">
      <header className="customer-quote__header"><span /><span /><span /></header>
      <div className="customer-quote__workspace"><aside /><div /></div>
    </div>
  );
}

type CustomerPreviewPanelProps = {
  preview: CustomerQuotePreview;
  customerPerStudent: string | null;
  unroundedPaymentTotalAmount?: string;
  standardAllocation?: CustomerQuoteAllocationResult | null;
  standardAllocationPending?: boolean;
  standardFieldVisibility: Record<CustomerQuoteStandardExportField, boolean>;
  versionLabel: string;
  pointSourceLines: CustomerQuoteLine[];
  onToggleStandardField: (field: CustomerQuoteStandardExportField) => void;
};

/** 客户安全预览；标准模板额外嵌入纯计算分摊结果，但不改写安全 DTO。 */
function CustomerPreviewPanel({
  preview,
  customerPerStudent,
  unroundedPaymentTotalAmount,
  standardAllocation = null,
  standardAllocationPending = false,
  standardFieldVisibility,
  onToggleStandardField,
  versionLabel,
  pointSourceLines,
}: CustomerPreviewPanelProps) {
  if (preview.templateType === "standard" && standardAllocation) {
    const groups = buildCustomerQuoteFeePreviewGroups(standardAllocation.breakdown, pointSourceLines);
    const showRounding = standardFieldVisibility.quoteAmount;
    const hasAgentCollection = compareCustomerQuoteMoney(preview.agentCollectionTotalAmount) > 0;
    return <div className="customer-quote-fees" aria-label="标准报价">
      <header className="customer-quote-fees__header">
        <div><span>行程报价 · {versionLabel}</span><h2>对客费用明细</h2></div>
        <Button type="outline" size="small" aria-pressed={showRounding}
          title="仅控制当前页面预览，不影响复制或 Excel 内容"
          onClick={() => onToggleStandardField("quoteAmount")}>
          {showRounding ? "隐藏取整金额" : "展开取整金额"}
        </Button>
      </header>
      <div className="customer-quote-fees__summary">
        <div aria-label="预览人均单价"><span>{hasAgentCollection ? "对客人均（含代收代付）" : "全程对客人均单价"}</span>
          <strong>{standardAllocationPending ? "试算中" : customerPerStudent === null ? "—" : formatCustomerQuoteMoney(customerPerStudent, true)}<small>/人</small></strong></div>
        <div className="customer-quote-fees__payer-count"><span>付费人数</span><strong>{preview.studentCount + (preview.teacherPays ? preview.teacherCount : 0)}<small> 人</small></strong></div>
        <div className="customer-quote-fees__quote-total" aria-label="预览报价总价"><span>对客报价总价</span><strong>{standardAllocationPending ? "试算中" : formatCustomerQuoteMoney(preview.customerPaymentTotalAmount, true)}</strong></div>
      </div>
      <div className="customer-quote-fees__groups" aria-label="标准报价分摊明细">
        {groups.map((group) => {
          const label = feeCategoryLabels[group.category as CustomerQuoteLine["feeCategory"]] ?? group.category;
          const periods = new Set(group.rows.map((row) => row.period));
          const periodHeading = periods.size === 1 && periods.has("晚") ? "晚数" : periods.size === 1 && periods.has("天") ? "天数" : "计费周期";
          return <section className="customer-quote-fees__group" key={group.category}>
            <div className="customer-quote-fees__heading"><h3>{label}</h3><span>对客报价 <b>{formatCustomerQuoteMoney(group.total, true)}</b></span></div>
            <div className="customer-quote-fees__scroll"><table aria-label={`${label}费用明细`} className={`customer-quote-fees__detail-table${showRounding ? "" : " is-compact"}${group.hasPoints ? " has-points" : ""}`}>
              <colgroup>{group.hasPoints ? <col className="fee-col-location" /> : null}<col className="fee-col-name" /><col className="fee-col-period" /><col className="fee-col-price" /><col className="fee-col-person" /><col className="fee-col-total" />{showRounding ? <><col className="fee-col-rounding" /><col className="fee-col-rounding" /></> : null}</colgroup>
              <thead><tr>{group.hasPoints ? <th scope="col">{group.category === "stay" ? "区域" : "考察点"}</th> : null}<th scope="col">费用名称</th><th scope="col">{periodHeading}</th><th scope="col">单价</th><th scope="col">单人总额</th><th scope="col">总金额</th>
                {showRounding ? <><th scope="col">取整前金额</th><th scope="col">取整后金额</th></> : null}</tr></thead>
              <tbody>{group.rows.map((row, index) => <tr key={`${row.customerName}:${index}`}>
                {group.hasPoints && row.pointRowSpan > 0 ? <td className="customer-quote-fees__point" rowSpan={row.pointRowSpan}>{row.pointLabel || "—"}</td> : null}
                <td className="customer-quote-fees__name">{row.customerName}</td>
                {row.amountRowSpan > 0 ? <>
                  <td rowSpan={row.amountRowSpan}>{row.period === "全程" ? "全程" : `${row.periodCount} ${row.period}`}</td>
                  <td rowSpan={row.amountRowSpan} className="customer-quote-fees__price" title={row.period === "全程" ? "全程每人报价" : `每人每${row.period}报价；展示到分，总金额以试算结果为准`}>{formatCustomerQuoteMoney(row.periodPrice, true)}</td>
                  <td rowSpan={row.amountRowSpan} title="本项学生全程报价，不用展示到分的周期单价反算">{formatCustomerQuoteMoney(row.studentUnitPrice, true)}</td>
                  <td rowSpan={row.amountRowSpan}>{formatCustomerQuoteMoney(row.roundedAmount, true)}<small className="customer-quote-fees__calculation">{describeCustomerQuoteFeeTotal(row, preview)}</small></td>
                  {showRounding ? <><td rowSpan={row.amountRowSpan}>{formatCustomerQuoteMoney(row.quoteAmount, true)}</td><td rowSpan={row.amountRowSpan}>{formatCustomerQuoteMoney(row.roundedAmount, true)}</td></> : null}
                </> : null}
              </tr>)}</tbody>
            </table></div>
            <p className="customer-quote-fees__calculation-note">单价＝单人总额÷计费周期（展示到分）；单人总额为学生全程报价，总金额按实际承担人数计算，整项费用另计。</p>
          </section>;
        })}
        {!groups.length ? <p className="customer-quote-fees__note">当前没有可展示的分摊明细，合并金额已计入总额。</p> : null}
        {preview.lines.filter((line) => line.isAgentCollection).map((line, index) => <section className="customer-quote-fees__group" key={`agent:${index}`}>
          <div className="customer-quote-fees__heading"><h3>{line.groupName}</h3><Tag color="orange">代收代付</Tag><span>对客报价 <b>{formatCustomerQuoteMoney(line.amount, true)}</b></span></div>
          {line.details?.length ? <div className="customer-quote-fees__scroll"><table aria-label={`${line.groupName}代收代付明细`}><thead><tr><th scope="col">费用名称</th><th scope="col">总金额</th></tr></thead>
            <tbody>{line.details.map((detail, detailIndex) => <tr key={detailIndex}><td>{detail.itemName}</td><td>{formatCustomerQuoteMoney(detail.amount, true)}</td></tr>)}</tbody></table></div> : null}
        </section>)}
      </div>
      {showRounding && !standardAllocationPending && unroundedPaymentTotalAmount !== undefined ? <p className="customer-quote-fees__note">客户支付总额（取整前 {formatCustomerQuoteMoney(unroundedPaymentTotalAmount)}）</p> : null}
      <p className="customer-quote-fees__note" aria-label="报价说明">{standardCustomerQuoteNote}</p>
      {preview.includedItems.length ? <p className="customer-quote-fees__note">费用包含：{preview.includedItems.join("、")}</p> : null}
      {preview.excludedItems.length ? <p className="customer-quote-fees__note">费用不含：{preview.excludedItems.join("、")}</p> : null}
    </div>;
  }
  const isStandardPreview = preview.templateType === "standard";
  const hasAgentCollection = compareCustomerQuoteMoney(preview.agentCollectionTotalAmount) > 0;
  const renderPreviewLine = (
    line: CustomerQuotePreview["lines"][number],
    index: number,
  ) => {
    const details = line.details ?? [];
    return (
      <div className="customer-quote-preview__group" key={`${line.groupName}:${index}`}>
        <div className="customer-quote-preview__line">
          <div>
            <strong>{line.groupName}</strong>
            {line.description ? <span>{line.description}</span> : null}
            {line.isAgentCollection ? <Tag color="orange">代收代付</Tag> : null}
          </div>
          <b>{formatCustomerQuoteMoney(line.amount)}</b>
        </div>
        {preview.templateType !== "package" && details.length ? (
          <ul className="customer-quote-preview__details" aria-label={`${line.groupName}费用明细`}>
            {details.map((detail, detailIndex) => (
              <li key={`${detail.itemName}:${detail.description}:${detailIndex}`}>
                <div>
                  <strong>{detail.itemName}</strong>
                  {detail.description ? <span>{detail.description}</span> : null}
                </div>
                <b>{formatCustomerQuoteMoney(detail.amount)}</b>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  };

  return (
    <div className={`customer-quote-preview is-${preview.templateType}`} aria-label={templateLabels[preview.templateType]}>
      <div className="customer-quote-preview__masthead">
        {preview.itineraryTitle ? (
          <div className="customer-quote-preview__itinerary" aria-label="客户报价行程">
            <span>项目行程</span>
            <strong>{preview.itineraryTitle}</strong>
          </div>
        ) : null}
        <div className="customer-quote-preview__template-label">
          <span>{templateLabels[preview.templateType]}</span>
        </div>
      </div>
      <div className="customer-quote-preview__people">
        <span>{preview.studentCount} 名学生</span>
        <span>{preview.teacherCount} 名老师</span>
      </div>
      <div className="customer-quote-preview__role-price">
          <div><span>{isStandardPreview ? hasAgentCollection ? "对客人均（含代收代付）" : "人均报价" : "学生报价"}</span><strong>{isStandardPreview && standardAllocationPending ? "试算中" : isStandardPreview ? customerPerStudent === null ? "—" : `${formatCustomerQuoteMoney(customerPerStudent)} / 人` : `${formatCustomerQuoteMoney(preview.studentUnitPrice)} / 人`}</strong></div>
          {!isStandardPreview && preview.teacherCount > 0 && preview.teacherPays ? (
            <div><span>老师报价</span><strong>{formatCustomerQuoteMoney(preview.teacherUnitPrice)} / 人</strong></div>
          ) : !isStandardPreview && preview.teacherCount <= 0 ? (
            <div><span>老师费用</span><strong>不适用</strong></div>
          ) : null}
      </div>
      {preview.teacherPaymentNote.trim() ? (
        <div
          className="customer-quote-preview__teacher-note"
          aria-label={isStandardPreview || (preview.teacherCount > 0 && !preview.teacherPays) ? "报价说明" : "老师费用说明"}
        >
          <span>{isStandardPreview || (preview.teacherCount > 0 && !preview.teacherPays) ? "报价说明" : "老师费用说明"}</span>
          <p>{isStandardPreview ? standardCustomerQuoteNote : preview.teacherPaymentNote}</p>
        </div>
      ) : null}
      <div className="customer-quote-preview__lines">{preview.lines.map(renderPreviewLine)}</div>
      <div className="customer-quote-preview__total">
        <span>客户支付总额</span>
        <div className="customer-quote-preview__total-amounts">
          <strong>{formatCustomerQuoteMoney(preview.customerPaymentTotalAmount)}</strong>

        </div>
      </div>
      {compareCustomerQuoteMoney(preview.agentCollectionTotalAmount) !== 0 ? (
        <div className="customer-quote-preview__agent">
          <span>其中代收代付</span><strong>{formatCustomerQuoteMoney(preview.agentCollectionTotalAmount)}</strong>
        </div>
      ) : null}
      {preview.includedItems.length ? <section><h3>费用包含</h3><p>{preview.includedItems.join("、")}</p></section> : null}
      {preview.excludedItems.length ? <section><h3>费用不含</h3><p>{preview.excludedItems.join("、")}</p></section> : null}
    </div>
  );
}

type StandardQuoteAllocationPanelProps = {
  allocation: CustomerQuoteAllocationResult | null;
  financialSummary: CustomerQuoteStandardFinancialSummary | null;
  loading: boolean;
  errorText: string | null;
};

/** 标准报价逐项整元试算；已保存版本会持久化结果，但不回写正式报价金额。 */
function StandardQuoteAllocationPanel({
  allocation,
  financialSummary,
  loading,
  errorText,
}: StandardQuoteAllocationPanelProps) {
  return (
    <section
      className="customer-quote-standard-allocation"
      role="region"
      aria-label="标准报价分摊试算"
    >
      <div className="customer-quote-standard-allocation__header">
        <div>
          <div className="customer-quote-standard-allocation__eyebrow">标准报价 · 逐项整元</div>
          <h3>人均分摊试算</h3>
          <p>按当前报价逐项向上取整；已保存版本会同步保存试算结果，但不修改正式报价金额。</p>
        </div>
        {loading ? <Tag color="arcoblue" role="status" aria-label="正在试算标准报价">自动计算中</Tag> : null}
      </div>

      {errorText ? <Alert type="error" showIcon content={`标准报价试算失败：${errorText}`} /> : null}

      {allocation && financialSummary ? (
        <>
          <div className="customer-quote-standard-allocation__metrics">
            <div><span>学生合同人均</span><strong>{formatCustomerQuoteMoney(allocation.studentUnitPrice, true)} / 人</strong></div>
            <div><span>老师合同人均</span><strong>{formatCustomerQuoteMoney(allocation.teacherUnitPrice, true)} / 人</strong></div>
            <div className="is-total"><span>标准报价总额</span><strong>{formatCustomerQuoteMoney(financialSummary.customerPaymentTotalAmount, true)}</strong></div>
            <div><span>预计毛利</span><strong>{formatCustomerQuoteMoney(financialSummary.expectedGrossProfitAmount, true)}</strong></div>
            <div><span>逐项取整上浮</span><strong>{formatCustomerQuoteMoney(allocation.upliftTotalAmount, true)}</strong></div>
            <div><span>未分摊金额</span><strong>{formatCustomerQuoteMoney(allocation.unallocatedAmount, true)}</strong></div>
          </div>
          {compareCustomerQuoteMoney(allocation.upliftTotalAmount) > 0 ? (
            <Alert
              type="info"
              showIcon
              content={`逐费用项整元取整使合同金额上浮 ${formatCustomerQuoteMoney(allocation.upliftTotalAmount, true)}。`}
            />
          ) : null}
          {compareCustomerQuoteMoney(allocation.unallocatedAmount) > 0 ? (
            <Alert
              type="warning"
              showIcon
              content={`仍有 ${formatCustomerQuoteMoney(allocation.unallocatedAmount, true)} 未进入学生或老师人均，已计入标准报价总额。`}
            />
          ) : null}
        </>
      ) : loading ? (
        <div className="customer-quote-standard-allocation__empty" aria-live="polite">正在按当前报价快照自动试算…</div>
      ) : (
        <div className="customer-quote-standard-allocation__empty">切换到标准报价或修改报价后，会自动计算人均、总额和预计毛利。</div>
      )}
    </section>
  );
}

/** 展示统一费用调整后的当前报价金额。 */
function CustomerQuoteSalesSummary({ summary, agentCollectionAmount, isLoading, errorMessage, onRetry, isRetrying }: {
  summary: ReturnType<typeof buildFeeAdjustmentQuoteSummary> | undefined;
  agentCollectionAmount: string;
  isLoading: boolean;
  errorMessage: string | null;
  onRetry: () => Promise<unknown>;
  isRetrying: boolean;
}) {
  const rows = summary ? [
    { key: "receivable", label: "应收总额", baseline: summary.frozenReceivableAmount, delta: summary.receivableDeltaAmount, current: summary.receivableTotalAmount },
    { key: "cost", label: "成本总额", baseline: summary.frozenCostAmount, delta: summary.costDeltaAmount, current: summary.internalCostTotalAmount },
    { key: "profit", label: "利润", baseline: summary.frozenProfitAmount, delta: summary.profitDeltaAmount, current: summary.grossProfitAmount },
  ] : [];
  return <section className="customer-quote-sales-summary" role="region" aria-label="报价、成本与利润">
    <div className="customer-quote__section-head"><div><h2>报价、成本与利润</h2><p>原报价叠加销售发起且计调已完成的费用调整；成本按实际成本变化累计。</p></div></div>
    {isLoading ? <div role="status">正在加载费用调整...</div> : null}
    {errorMessage ? <Alert type="error" content={<div className="customer-quote__recovery-actions"><span>{errorMessage}</span><Button loading={isRetrying} disabled={isRetrying} onClick={() => void onRetry()}>重试费用调整</Button></div>} /> : null}
    {summary && !isLoading && !errorMessage ? <>
      <div className="customer-quote-sales-summary__table-scroll">
        <table className="customer-quote-sales-summary__table" aria-label="费用调整金额核对">
          <thead><tr><th scope="col">金额项目</th><th scope="col">调整前原版</th><th scope="col">已完成费用调整净额</th><th scope="col">当前合计（调整后）</th></tr></thead>
          <tbody>{rows.map((row) => <tr key={row.key}>
            <th scope="row">{row.label}</th>
            <td data-testid={row.key === "receivable" ? "customer-quote-frozen-receivable" : undefined}>{formatCustomerQuoteMoney(row.baseline)}</td>
            <td>{formatFeeAdjustmentAmount(row.delta)}</td>
            <td data-testid={row.key === "receivable" ? "customer-quote-effective-receivable" : undefined}><strong>{formatCustomerQuoteMoney(row.current)}</strong></td>
          </tr>)}</tbody>
        </table>
      </div>
      <p className="customer-quote-sales-summary__state">当前客户支付总额 {formatCustomerQuoteMoney(summary.customerPaymentTotalAmount)} = 当前合同应收 {formatCustomerQuoteMoney(summary.receivableTotalAmount)} + 代收代付 {formatCustomerQuoteMoney(agentCollectionAmount)}。利润按客户支付总额减去当前成本计算。</p>
    </> : null}
  </section>;
}

/** 所有费用调整按请求展示，历史默认折叠。 */
function CustomerQuoteAdjustmentDetails({ records }: { records: FeeAdjustmentRecord[] }) {
  return <details className="customer-quote-adjustment-disclosure" role="region" aria-label="费用调整记录">
    <summary className="customer-quote-adjustment-disclosure__summary"><strong>费用调整记录</strong><span>{records.length} 笔 <ChevronDown aria-hidden="true" size={16} /></span></summary>
    <div className="customer-quote-adjustment-disclosure__body">
      {records.length ? [...records].sort((a, b) => b.created_at.localeCompare(a.created_at)).map((record) => <article className="customer-quote-fee-adjustment-record" key={record.request_id}>
        <header><strong>费用调整 · {record.request_id.slice(0, 8).toUpperCase()}</strong><Tag color={record.status === "processed" ? "green" : "arcoblue"}>{record.status === "processed" ? "计调已完成" : "待计调确认"}</Tag></header>
        <p>{record.request_text}</p>
        <p>{record.members.map((member) => `${member.member_name_snapshot}（${member.is_payer ? "付款" : "不付款"}）`).join("、")}</p>
        {record.processing_basis ? <p>处理依据：{record.processing_basis}</p> : null}
        {record.lines.length ? <table><thead><tr><th>费用项目</th><th>调整前</th><th>调整后</th><th>成本变化</th></tr></thead><tbody>{record.lines.map((line) => <tr key={line.id}><td>{line.item_name}</td><td>{quoteAdjustmentPricing(line.before)}</td><td>{quoteAdjustmentPricing(line.after)}</td><td>{formatCustomerQuoteMoney(line.cost_delta_amount)}</td></tr>)}</tbody></table> : <p>计调尚未录入费用明细</p>}
        <p>应收变化 {formatCustomerQuoteMoney(record.total_receivable_delta_amount)} · 成本变化 {formatCustomerQuoteMoney(record.total_cost_delta_amount)} · 利润变化 {formatCustomerQuoteMoney(record.total_profit_delta_amount)}</p>
      </article>) : <p>暂无费用调整记录</p>}
    </div>
  </details>;
}

/**
 *
 */
function quoteAdjustmentPricing(value: FeeAdjustmentRecord["lines"][number]["after"] | null): string {
  if (!value) return "新增费用";
  return `${value.quantity} ${value.unit} × ${value.billing_count} ${value.spec_unit} × ${formatCustomerQuoteMoney(value.unit_price_amount)} = ${formatCustomerQuoteMoney(value.amount)}`;
}

/** 行程对客报价编辑页。 */
export function CustomerQuoteEditorFeaturePage({
  customerQuoteId,
  feeAdjustmentContext: explicitFeeAdjustmentContext,
}: CustomerQuoteEditorFeaturePageProps) {
  const runtime = useCustomerQuoteRuntime(customerQuoteId);
  useBusinessPageTitle(runtime.editor?.customerQuoteId === customerQuoteId
    ? `报价 · ${runtime.editor.itineraryTitle || "未命名行程"} · V${runtime.editor.versionNo} ${runtime.editor.versionName}`
    : undefined);
  const unsavedChangesGuard = useUnsavedChangesGuard(runtime.isDirty);
  const previewBlockedBySource = Boolean(
    runtime.editor && runtime.editor.sourceState === "unavailable",
  );
  const previewBlockedByStayMarkup = Boolean(
    runtime.snapshot
    && hasHighCustomerQuoteStayMarkup(runtime.snapshot)
    && !runtime.snapshot.stayHighMarkupConfirmed,
  );
  const previewBlocked = previewBlockedBySource || previewBlockedByStayMarkup;
  const editorReady = Boolean(
    runtime.editor && runtime.snapshot && runtime.summary && runtime.roleSummary,
  );
  const pageRef = useRef<HTMLDivElement>(null);
  const pageTitleRef = useRef<HTMLHeadingElement>(null);
  const [renamingQuoteId, setRenamingQuoteId] = useState<string | null>(null);
  const [returningBudget, setReturningBudget] = useState(false);
  const [targetInput, setTargetInput] = useState<{ kind: "per_student_difference" | "margin_rate"; amount: string } | null>(null);
  useEffect(() => { setTargetInput(null); setReturningBudget(false); }, [customerQuoteId]);
  const actionErrorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const currentUserQuery = useCurrentAuthUser();
  const feeAdjustmentsQuery = useCustomerQuoteFeeAdjustments(
    customerQuoteId,
    explicitFeeAdjustmentContext,
    editorReady,
  );
  const feeAdjustmentContext = feeAdjustmentsQuery.context ?? undefined;
  const feeAdjustmentRecords = feeAdjustmentsQuery.data ?? [];

  const canReturnBudget = canUsePermission(currentUserQuery.data, "opportunity.customer_quote:return_budget");
  const canUpdate = canUsePermission(
    currentUserQuery.data,
    "opportunity.customer_quote:update",
  );
  const canAutomaticallyCalculate = !currentUserQuery.isLoading
    && !currentUserQuery.isError
    && canUpdate;
  const canAutomaticallyCalculateRef = useRef(canAutomaticallyCalculate);
  useLayoutEffect(() => {
    canAutomaticallyCalculateRef.current = canAutomaticallyCalculate;
  }, [canAutomaticallyCalculate]);
  const canExport = canUsePermission(
    currentUserQuery.data,
    "opportunity.customer_quote:export",
  );
  const canAccessBudget = canUsePermission(currentUserQuery.data, "opportunity.quote_budget:access");
  const canAccessRoster = canUsePermission(currentUserQuery.data, "customization.order_handover:access");
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string> | null>(null);
  const [amountDetailKeys, setAmountDetailKeys] = useState<Set<string>>(() => new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [calculationOpen, setCalculationOpen] = useState(false);
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);
  const [previewStale, setPreviewStale] = useState(false);
  const initialPreviewTemplateType = runtime.previewData?.templateType ?? runtime.snapshot?.templateType ?? null;
  const [desiredPreviewTemplateType, setDesiredPreviewTemplateType] = useState<CustomerQuoteTemplateType | null>(initialPreviewTemplateType);
  const previousSnapshotRef = useRef(runtime.snapshot);
  const previewRef = useRef(runtime.preview);
  const previewRevisionRef = useRef(0);
  const scheduledAutoPreviewTimerRef = useRef<number | null>(null);
  const skipNextAutoPreviewRef = useRef(false);
  const desiredPreviewTemplateRef = useRef<CustomerQuoteTemplateType | null>(initialPreviewTemplateType);
  const previewCustomerQuoteIdRef = useRef(customerQuoteId);
  const pendingTemplateSwitchRef = useRef<PendingTemplateSwitch | null>(null);
  const pendingConflictReloadSnapshotRef = useRef<CustomerQuoteSnapshot | null>(null);
  const [lineAmountDrafts, setLineAmountDrafts] = useState<Record<number, string>>({});
  const [lineAmountRevisions, setLineAmountRevisions] = useState<Record<number, number>>({});
  const [lineMarkupDrafts, setLineMarkupDrafts] = useState<Record<number, string>>({});
  const [lineMarkupRevisions, setLineMarkupRevisions] = useState<Record<number, number>>({});
  const [lineMarkupScopeRevision, setLineMarkupScopeRevision] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [copyingCustomerQuote, setCopyingCustomerQuote] = useState(false);
  const [restoringSavedDraft, setRestoringSavedDraft] = useState(false);
  const [standardFieldVisibility, setStandardFieldVisibility] = useState<
    Record<CustomerQuoteStandardExportField, boolean>
  >({ ...defaultStandardFieldVisibility });
  const openCustomerPreview = () => {
    setStandardFieldVisibility({ ...defaultStandardFieldVisibility });
    setPreviewOpen(true);
  };
  const lineAmountInitialValuesRef = useRef(new Map<number, string>());
  const lineAmountLastCommittedRef = useRef(new Map<number, string>());
  const lineAmountDraftsRef = useRef(lineAmountDrafts);
  const focusedAmountLineRef = useRef<number | null>(null);
  const lineMarkupInitialValuesRef = useRef(new Map<number, string>());
  const lineMarkupLastCommittedRef = useRef(new Map<number, string>());
  const lineMarkupDraftsRef = useRef(lineMarkupDrafts);
  const focusedMarkupLineRef = useRef<number | null>(null);
  const rawInputInvalidRef = useRef<Record<string, boolean>>({});
  const pageActionRequestRef = useRef(0);
  const copyMarkdownInFlightRef = useRef(false);
  const authoritativeRevisionRef = useRef<string | null>(null);

  useLayoutEffect(() => () => {
    pageActionRequestRef.current += 1;
  }, [customerQuoteId]);

  useEffect(() => {
    setStandardFieldVisibility({ ...defaultStandardFieldVisibility });
    setExpandedGroupKeys(null);
    setAmountDetailKeys(new Set());
  }, [customerQuoteId]);


  useEffect(() => {
    lineMarkupDraftsRef.current = lineMarkupDrafts;
  }, [lineMarkupDrafts]);

  useEffect(() => {
    lineAmountDraftsRef.current = lineAmountDrafts;
  }, [lineAmountDrafts]);

  useEffect(() => {
    focusedAmountLineRef.current = null;
    lineAmountDraftsRef.current = {};
    setLineAmountDrafts({});
    setLineAmountRevisions({});
    lineAmountInitialValuesRef.current.clear();
    lineAmountLastCommittedRef.current.clear();
    focusedMarkupLineRef.current = null;
    lineMarkupDraftsRef.current = {};
    setLineMarkupDrafts({});
    setLineMarkupRevisions({});
    setLineMarkupScopeRevision((current) => current + 1);
    lineMarkupInitialValuesRef.current.clear();
    lineMarkupLastCommittedRef.current.clear();
    rawInputInvalidRef.current = {};
    setFieldErrors({});
  }, [customerQuoteId]);

  useEffect(() => {
    const quoteRevision = runtime.editor?.quoteRevision;
    const sourceRevision = runtime.editor?.sourceRevision ?? "";
    if (!quoteRevision || runtime.isDirty) return;
    const authoritativeRevision = `${customerQuoteId}:${quoteRevision}:${sourceRevision}`;
    if (authoritativeRevisionRef.current === authoritativeRevision) return;
    authoritativeRevisionRef.current = authoritativeRevision;
    rawInputInvalidRef.current = {};
    setFieldErrors({});
  }, [
    customerQuoteId,
    runtime.editor?.quoteRevision,
    runtime.editor?.sourceRevision,
    runtime.isDirty,
  ]);

  useEffect(() => {
    const activeLineNo = focusedAmountLineRef.current;
    const activeLine = runtime.snapshot?.lines.find((line) => line.lineNo === activeLineNo);
    const activeDraft = activeLineNo === null ? undefined : lineAmountDraftsRef.current[activeLineNo];
    const min = activeLine?.lineOrigin === "budget" ? 0 : undefined;
    const normalizedDraft = activeDraft === undefined
      ? null
      : normalizeQuoteAmount(activeDraft, min);
    const normalizedAmount = activeLine
      ? normalizeQuoteAmount(activeLine.quoteAmount, min)
      : null;
    const preserveActiveEdit = Boolean(
      activeLine
      && normalizedDraft !== null
      && normalizedDraft === normalizedAmount,
    );
    const initialValue = preserveActiveEdit && activeLineNo !== null
      ? lineAmountInitialValuesRef.current.get(activeLineNo)
      : undefined;
    const lastCommitted = preserveActiveEdit && activeLineNo !== null
      ? lineAmountLastCommittedRef.current.get(activeLineNo)
      : undefined;

    lineAmountDraftsRef.current = {};
    setLineAmountDrafts({});
    setLineAmountRevisions({});
    lineAmountInitialValuesRef.current.clear();
    lineAmountLastCommittedRef.current.clear();
    if (preserveActiveEdit && activeLineNo !== null && activeDraft !== undefined) {
      lineAmountDraftsRef.current = { [activeLineNo]: activeDraft };
      setLineAmountDrafts({ [activeLineNo]: activeDraft });
      if (initialValue !== undefined) {
        lineAmountInitialValuesRef.current.set(activeLineNo, initialValue);
      }
      if (lastCommitted !== undefined) {
        lineAmountLastCommittedRef.current.set(activeLineNo, lastCommitted);
      }
    }
  }, [runtime.snapshot]);

  useEffect(() => {
    const activeLineNo = focusedMarkupLineRef.current;
    const activeLine = runtime.snapshot?.lines.find((line) => line.lineNo === activeLineNo);
    const activePricingGroup = activeLineNo === null || !runtime.snapshot
      ? undefined
      : buildCustomerQuotePricingGroups(
          runtime.snapshot.lines,
          runtime.snapshot.pricingRulesVersion,
        )
          .find((group) => group.lineNos.includes(activeLineNo));
    const activeDraft = activeLineNo === null ? undefined : lineMarkupDraftsRef.current[activeLineNo];
    const normalizedDraft = activeDraft === undefined
      ? null
      : normalizeCustomerQuoteMoney(activeDraft);
    const preserveActiveEdit = Boolean(
      activeLine
      && normalizedDraft !== null
      && normalizedDraft === (
        activePricingGroup
          ? customerQuotePricingGroupBaseMarkup(activePricingGroup)
          : customerQuoteUnitMarkup(activeLine)
      ),
    );
    const initialValue = preserveActiveEdit && activeLineNo !== null
      ? lineMarkupInitialValuesRef.current.get(activeLineNo)
      : undefined;
    const lastCommitted = preserveActiveEdit && activeLineNo !== null
      ? lineMarkupLastCommittedRef.current.get(activeLineNo)
      : undefined;

    lineMarkupDraftsRef.current = {};
    setLineMarkupDrafts({});
    setLineMarkupRevisions({});
    if (!preserveActiveEdit) {
      setLineMarkupScopeRevision((current) => current + 1);
    }
    lineMarkupInitialValuesRef.current.clear();
    lineMarkupLastCommittedRef.current.clear();
    if (preserveActiveEdit && activeLineNo !== null) {
      if (initialValue !== undefined) {
        lineMarkupInitialValuesRef.current.set(activeLineNo, initialValue);
      }
      if (lastCommitted !== undefined) {
        lineMarkupLastCommittedRef.current.set(activeLineNo, lastCommitted);
      }
    }
  }, [runtime.snapshot]);

  useEffect(() => {
    previewRef.current = runtime.preview;
  }, [runtime.preview]);

  /** 当前替代预览成功后，提交仍在等待确认的持久模板切换。 */
  const commitPendingTemplateSwitch = useCallback((templateType: CustomerQuoteTemplateType) => {
    const pendingSwitch = pendingTemplateSwitchRef.current;
    if (
      pendingSwitch?.customerQuoteId === customerQuoteId
      && pendingSwitch.actionRequestId === pageActionRequestRef.current
      && pendingSwitch.requestedTemplateType === templateType
    ) {
      pendingTemplateSwitchRef.current = null;
    }
  }, [customerQuoteId]);

  /** 当前替代预览失败时，仅回滚模板字段，保留期间发生的金额或展示修改。 */
  const rollbackPendingTemplateSwitch = useCallback((templateType: CustomerQuoteTemplateType) => {
    const pendingSwitch = pendingTemplateSwitchRef.current;
    if (
      pendingSwitch?.customerQuoteId !== customerQuoteId
      || pendingSwitch.actionRequestId !== pageActionRequestRef.current
      || pendingSwitch.requestedTemplateType !== templateType
    ) {
      return false;
    }
    pendingTemplateSwitchRef.current = null;
    desiredPreviewTemplateRef.current = pendingSwitch.successfulTemplateType;
    setDesiredPreviewTemplateType(pendingSwitch.successfulTemplateType);
    skipNextAutoPreviewRef.current = true;
    pendingSwitch.rollbackPersistedTemplate();
    return true;
  }, [customerQuoteId]);

  useEffect(() => {
    if (previewCustomerQuoteIdRef.current === customerQuoteId) return;
    previewCustomerQuoteIdRef.current = customerQuoteId;
    pendingTemplateSwitchRef.current = null;
    pendingConflictReloadSnapshotRef.current = null;
    skipNextAutoPreviewRef.current = false;
    desiredPreviewTemplateRef.current = null;
    setDesiredPreviewTemplateType(null);
    previewRevisionRef.current += 1;
  }, [customerQuoteId]);

  useEffect(() => {
    const scrollContainer = pageRef.current?.closest(".arco-layout-content");
    if (!(scrollContainer instanceof HTMLElement)) {
      setShowBackToTop(false);
      return;
    }
    const updateVisibility = () => setShowBackToTop(scrollContainer.scrollTop > 240);
    updateVisibility();
    scrollContainer.addEventListener("scroll", updateVisibility, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", updateVisibility);
  }, [customerQuoteId, editorReady]);

  /** 刷新客户安全预览并标记当前预览为最新。 */
  const refreshPreview = useCallback(async (
    templateType?: CustomerQuoteTemplateType,
    snapshotToPreview?: typeof runtime.snapshot,
    rollbackTemplateType?: CustomerQuoteTemplateType,
  ) => {
    if (previewBlockedBySource) {
      throw new Error(runtime.editor?.sourceBlockReason || "来源核价当前不可用，请稍后刷新后重试。");
    }
    if (previewBlockedByStayMarkup) {
      throw new Error("住宿每人晚高加价尚未人工确认，不能生成客户报价。");
    }
    if (scheduledAutoPreviewTimerRef.current !== null) {
      window.clearTimeout(scheduledAutoPreviewTimerRef.current);
      scheduledAutoPreviewTimerRef.current = null;
    }
    const effectiveTemplateType = templateType
      ?? desiredPreviewTemplateRef.current
      ?? runtime.previewData?.templateType
      ?? runtime.snapshot?.templateType;
    if (!effectiveTemplateType) throw new Error("报价编辑器尚未就绪");
    desiredPreviewTemplateRef.current = effectiveTemplateType;
    setDesiredPreviewTemplateType(effectiveTemplateType);
    const revision = previewRevisionRef.current + 1;
    previewRevisionRef.current = revision;
    setPreviewStale(true);
    try {
      const result = snapshotToPreview
        ? await runtime.preview(effectiveTemplateType, snapshotToPreview)
        : await runtime.preview(effectiveTemplateType);
      if (previewRevisionRef.current === revision) {
        commitPendingTemplateSwitch(effectiveTemplateType);
      }
      return result;
    } catch (error) {
      if (previewRevisionRef.current === revision) {
        const rolledBackPendingSwitch = rollbackPendingTemplateSwitch(effectiveTemplateType);
        if (!rolledBackPendingSwitch && rollbackTemplateType) {
          desiredPreviewTemplateRef.current = rollbackTemplateType;
          setDesiredPreviewTemplateType(rollbackTemplateType);
        }
      }
      throw error;
    } finally {
      if (previewRevisionRef.current === revision) setPreviewStale(false);
    }
  }, [
    commitPendingTemplateSwitch,
    previewBlockedBySource,
    previewBlockedByStayMarkup,
    rollbackPendingTemplateSwitch,
    runtime.editor?.sourceBlockReason,
    runtime.preview,
    runtime.previewData?.templateType,
    runtime.snapshot?.templateType,
  ]);

  /** 客户预览成功后按模板同步标准分摊；试算失败不回滚已成功的安全预览。 */
  const syncStandardAllocation = useCallback(async (templateType: CustomerQuoteTemplateType) => {
    if (templateType === "standard" && canAutomaticallyCalculateRef.current) {
      await runtime.calculateStandardAllocation().catch(() => undefined);
      return;
    }
    runtime.invalidateStandardAllocation();
  }, [runtime.calculateStandardAllocation, runtime.invalidateStandardAllocation]);
  const syncStandardAllocationRef = useRef(syncStandardAllocation);
  useLayoutEffect(() => {
    syncStandardAllocationRef.current = syncStandardAllocation;
  }, [syncStandardAllocation]);

  const previousCalculationPermissionRef = useRef(canAutomaticallyCalculate);
  useEffect(() => {
    const previouslyAllowed = previousCalculationPermissionRef.current;
    previousCalculationPermissionRef.current = canAutomaticallyCalculate;
    if (!canAutomaticallyCalculate) {
      runtime.invalidateStandardAllocation();
    } else if (
      !previouslyAllowed
      && runtime.previewData?.templateType === "standard"
      && !runtime.previewing
      && !previewStale
      && !previewBlocked
    ) {
      void syncStandardAllocation("standard");
    }
  }, [
    canAutomaticallyCalculate,
    previewBlocked,
    previewStale,
    runtime.invalidateStandardAllocation,
    runtime.previewData?.templateType,
    runtime.previewing,
    syncStandardAllocation,
  ]);

  /** 冲突重载返回后先采用服务端模板，避免自动预览沿用已放弃草稿的模板。 */
  useEffect(() => {
    const authoritativeTemplateType = runtime.snapshot?.templateType;
    const conflictBaselineSnapshot = pendingConflictReloadSnapshotRef.current;
    if (
      !authoritativeTemplateType
      || runtime.isDirty
      || !conflictBaselineSnapshot
      || runtime.snapshot === conflictBaselineSnapshot
    ) {
      return;
    }
    pendingConflictReloadSnapshotRef.current = null;
    pendingTemplateSwitchRef.current = null;
    skipNextAutoPreviewRef.current = false;
    desiredPreviewTemplateRef.current = authoritativeTemplateType;
    setDesiredPreviewTemplateType((current) => (
      current === authoritativeTemplateType ? current : authoritativeTemplateType
    ));
  }, [customerQuoteId, runtime.isDirty, runtime.snapshot]);

  useEffect(() => {
    if (runtime.editor?.customerQuoteId !== customerQuoteId) {
      previousSnapshotRef.current = null;
      setPreviewStale(false);
      return;
    }
    const previousSnapshot = previousSnapshotRef.current;
    previousSnapshotRef.current = runtime.snapshot;
    if (previewBlocked) {
      const pendingSwitch = pendingTemplateSwitchRef.current;
      if (pendingSwitch) {
        previewRevisionRef.current += 1;
        runtime.invalidatePreview();
        rollbackPendingTemplateSwitch(pendingSwitch.requestedTemplateType);
      }
      skipNextAutoPreviewRef.current = false;
      setPreviewStale(false);
      return;
    }
    if (!previousSnapshot || !runtime.snapshot) {
      setPreviewStale(false);
      return;
    }
    if (previousSnapshot === runtime.snapshot) return;
    if (skipNextAutoPreviewRef.current) {
      skipNextAutoPreviewRef.current = false;
      return;
    }

    runtime.invalidatePreview();
    const revision = previewRevisionRef.current + 1;
    previewRevisionRef.current = revision;
    const snapshotToPreview = runtime.snapshot;
    const templateType = desiredPreviewTemplateRef.current ?? snapshotToPreview.templateType;
    setPreviewStale(true);
    const timer = window.setTimeout(() => {
      if (scheduledAutoPreviewTimerRef.current === timer) {
        scheduledAutoPreviewTimerRef.current = null;
      }
      void previewRef.current(templateType, snapshotToPreview)
        .then(async () => {
          if (previewRevisionRef.current !== revision) return;
          commitPendingTemplateSwitch(templateType);
          // 备注或名称改变只更新执行回调，不能取消同一金额快照的防抖任务。
          await syncStandardAllocationRef.current(templateType);
          if (previewRevisionRef.current !== revision) return;
          setPreviewStale(false);
        })
        .catch(() => {
          if (previewRevisionRef.current !== revision) return;
          if (!rollbackPendingTemplateSwitch(templateType)) {
            const rollbackTemplateType = runtime.previewData?.templateType
              ?? snapshotToPreview.templateType;
            desiredPreviewTemplateRef.current = rollbackTemplateType;
            setDesiredPreviewTemplateType(rollbackTemplateType);
          }
          setPreviewStale(false);
        });
    }, 120);
    scheduledAutoPreviewTimerRef.current = timer;
    return () => {
      window.clearTimeout(timer);
      if (scheduledAutoPreviewTimerRef.current === timer) {
        scheduledAutoPreviewTimerRef.current = null;
      }
    };
  }, [
    commitPendingTemplateSwitch,
    customerQuoteId,
    previewBlocked,
    rollbackPendingTemplateSwitch,
    runtime.invalidatePreview,
    runtime.editor?.customerQuoteId,
    runtime.snapshot,
  ]);

  useEffect(() => {
    if (
      runtime.editor?.customerQuoteId === customerQuoteId
      && runtime.snapshot
      && !previewBlocked
      && !runtime.previewData
      && !runtime.previewing
      && !runtime.previewError
    ) {
      void refreshPreview()
        .then((result) => syncStandardAllocation(result.templateType))
        .catch(() => undefined);
    }
  }, [customerQuoteId, previewBlocked, refreshPreview, runtime, syncStandardAllocation]);

  if (runtime.isOffline && !runtime.editor) {
    return <div className="customer-quote__load-error" role="status"><span>网络连接已暂停，联网后将自动加载报价。</span><Button onClick={() => navigate("/product-lines/course-routes", { replace: true })}>返回课程线路</Button></div>;
  }
  if (runtime.error) {
    return (
      <>
        {unsavedChangesGuard.blocker}
        <div className="customer-quote__load-error" role="alert">
          <span>{customerQuoteLoadErrorText(runtime.error)}</span>
          <Button
            icon={<ArrowLeft size={16} />}
            onClick={() => navigate("/product-lines/course-routes", { replace: true })}
          >
            返回课程线路
          </Button>
          {customerQuoteLoadErrorCanRetry(runtime.error) ? (
            <Button
              type="primary"
              loading={runtime.reloading}
              disabled={runtime.reloading}
              onClick={() => void runtime.reload().catch(() => undefined)}
            >
              重新加载
            </Button>
          ) : null}
        </div>
      </>
    );
  }
  if (!runtime.editor || !runtime.snapshot || !runtime.summary || !runtime.roleSummary) {
    return <>{unsavedChangesGuard.blocker}<LoadingWorkbench /></>;
  }

  const { editor, snapshot, summary, roleSummary } = runtime;
  const isDraft = editor.status === "draft";
  const otherActionBusy = returningBudget || copyingCustomerQuote
    || restoringSavedDraft
    || runtime.saving
    || runtime.generatingAutoTarget
    || runtime.repricingTarget
    || runtime.finalizing
    || runtime.downloading
    || runtime.exportingMarkdown
    || runtime.loadingLatestAfterConflict;
  const actionBusy = otherActionBusy || runtime.confirmingSourceChange;
  const writeActionBusy = actionBusy
    || runtime.previewing
    || previewStale
    || Boolean(runtime.writeConflict);
  const writeActionBlockedReason = runtime.loadingLatestAfterConflict
    ? "正在加载最新报价版本，请稍后操作"
    : runtime.writeConflict
      ? "报价版本已发生冲突，请先加载最新版本"
      : runtime.previewing
        ? "客户预览生成中，请稍后操作"
        : previewStale
          ? "客户预览同步中，请稍后操作"
        : actionBusy
          ? "当前操作正在进行，请稍后重试"
          : undefined;
  const sourceChanges = editor.sourceChanges ?? [];
  const headerSourceChanges = sourceChanges.filter((change) => change.changeType === "header_modified");
  const addedSourceChanges = sourceChanges.filter((change) => change.changeType === "added");
  const hiddenTaxSourceChanges = sourceChanges.filter((change) => (
    change.feeCategory === "tax"
    && change.changeType !== "added"
    && change.changeType !== "header_modified"
  ));
  const addedSourceChangeGroups = addedSourceChanges.reduce<Record<string, CustomerQuoteSourceChange[]>>(
    (result, change) => {
      const category = change.feeCategory ?? "misc";
      (result[category] ??= []).push(change);
      return result;
    },
    {},
  );
  const editingDisabled = runtime.readonly || !editor.canEditQuote || otherActionBusy || !canUpdate;
  const validation = validateCustomerQuoteDraft(snapshot);
  const requiresStayHighMarkupConfirmation = hasHighCustomerQuoteStayMarkup(snapshot);
  const finalizeErrors = isDraft
    ? [...validation.draftErrors, ...validation.finalizeErrors]
    : [];
  const customerOutputBlockedByStayMarkup = isDraft && previewBlockedByStayMarkup;
  const visibleWriteActionError = runtime.writeConflict
    ? null
    : (isDraft && (runtime.saveError || runtime.finalizeError))
        || runtime.confirmSourceChangeError
      || runtime.generateAutoTargetError;
  const visibleActionError = visibleWriteActionError
    || runtime.editError
    || runtime.downloadError;
  const groups = groupCustomerQuoteLines(snapshot);
  /** 用户主动定位变化时展开所在组，并把键盘焦点带到该项。 */
  function locateSourceChange(change: CustomerQuoteSourceChange) {
    const group = change.sourceBudgetLineId && groups.find((candidate) => candidate.lineNos.some((lineNo) => (
      snapshot.lines.find((line) => line.lineNo === lineNo)?.sourceBudgetLineId === change.sourceBudgetLineId
    )));
    if (group) setExpandedGroupKeys((current) => new Set([...(current ?? groups.map((item) => item.key)), group.key]));
    requestAnimationFrame(() => {
      const target = document.getElementById(`customer-quote-change-${change.changeId}`);
      target?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      target?.focus({ preventScroll: true });
    });
  }
  /** 明确操作失败时聚焦提示；后台预览不会触发此焦点移动。 */
  function showActionFailure(error: unknown) {
    Message.error(customerQuoteActionErrorText(error, "客户报价操作失败，请重试。", editor.sourceBlockReason, editor.sourceState));
    requestAnimationFrame(() => actionErrorRef.current?.focus({ preventScroll: true }));
  }
  const saveDisabled = Boolean(
    !canUpdate
    || runtime.readonly
    || !editor.canSave
    || !runtime.isDirty
    || writeActionBusy
    || runtime.versionNameError
    || validation.draftErrors.length,
  );
  const finalizeDisabled = Boolean(
    !canUpdate
    || runtime.readonly
    || !editor.canFinalize
    || runtime.isDirty
    || writeActionBusy
    || runtime.versionNameError
    || finalizeErrors.length,
  );
  const finalizeBlockedReason = runtime.versionNameError
    ?? (runtime.isDirty ? "请先保存当前草稿再定稿" : writeActionBlockedReason);
  const canConfirmSourceChange = Boolean(
    canUpdate
    && editor.canConfirmSourceChange
    && !otherActionBusy && !runtime.writeConflict,
  );
  const previewActionsDisabled = Boolean(
    previewBlockedBySource || validation.draftErrors.length || customerOutputBlockedByStayMarkup || actionBusy,
  );
  const templatePreviewSwitchDisabled = previewActionsDisabled
    || runtime.previewing
    || runtime.calculatingStandardAllocation
    || previewStale;
  const exportBlockedBySource = editor.sourceState !== "finalized";
  const downloadDisabled = Boolean(
    !canExport
    || exportBlockedBySource
    || validation.draftErrors.length
    || customerOutputBlockedByStayMarkup
    || runtime.previewing
    || previewStale
    || !runtime.previewIsCurrent
    || runtime.versionNameError
    || actionBusy,
  );
  const outputBlockedReason = runtime.versionNameError
    ?? (exportBlockedBySource ? editor.sourceBlockReason ?? undefined : undefined)
    ?? (!runtime.previewIsCurrent ? "当前草稿尚未成功同步客户预览，请点击“客户预览”同步后再复制或下载。" : undefined);
  const activePreviewTemplateType = desiredPreviewTemplateType
    ?? runtime.previewData?.templateType
    ?? snapshot.templateType;
  const successfulPreviewTemplateType = runtime.previewData?.templateType ?? snapshot.templateType;
  const standardAllocationPending = activePreviewTemplateType === "standard"
    && canAutomaticallyCalculate
    && (runtime.previewing || previewStale || runtime.calculatingStandardAllocation);
  const standardAllocationErrorText = runtime.standardAllocationError
    ? customerQuoteActionErrorText(
        runtime.standardAllocationError,
        "标准报价试算请求失败，请检查当前报价后重试。",
        editor.sourceBlockReason,
        editor.sourceState,
      )
    : null;
  const isRosterPricingLocked = editor.isRosterPricingLocked;
  const roundedPresentation = runtime.standardAllocation
    ? buildCustomerQuoteRoundedPresentation(snapshot, runtime.standardAllocation, { preserveFrozenAmounts: isRosterPricingLocked })
    : null;
  const standardAllocation = roundedPresentation?.allocation ?? runtime.standardAllocation;
  const standardFinancialSummary = roundedPresentation?.financialSummary ?? runtime.standardFinancialSummary;
  const useRoundedSummary = successfulPreviewTemplateType === "standard" && Boolean(standardAllocation);
  const contractSummaryGroups = useRoundedSummary && roundedPresentation?.groups ? roundedPresentation.groups : groups.map((group) => ({
    ...group, ...calculateCustomerQuoteRoleSummary({ ...snapshot, lines: snapshot.lines.filter((line) => group.lineNos.includes(line.lineNo)) }),
  }));
  const hasAgentCollection = snapshot.lines.some((line) => line.financeTreatment === "agent_collection");
  const agentPerStudent = calculateCustomerQuoteAgentPerStudent(snapshot);
  const summaryGroups = contractSummaryGroups.map((group) => {
    const lines = snapshot.lines.filter((line) => group.lineNos.includes(line.lineNo));
    const includesAgent = lines.some((line) => line.financeTreatment === "agent_collection");
    const agents = calculateCustomerQuoteAgentPresentation({ ...snapshot, lines });
    return { ...group, includesAgent, teacherUnitPrice: addCustomerQuoteAgentPerStudent(group.teacherUnitPrice, agents.teacherUnitPrice)!, studentUnitPrice: includesAgent
      ? addCustomerQuoteAgentPerStudent(group.studentUnitPrice, agents.studentUnitPrice)
      : snapshot.studentCount > 0 ? group.studentUnitPrice : null };
  });
  const groupAmountState = standardAllocationPending ? "试算中" : useRoundedSummary && !roundedPresentation?.groups ? "取整明细不可用" : null;
  const displayedCustomerPreview = runtime.previewData?.templateType === "standard"
    && standardAllocation
    && standardFinancialSummary
    ? {
        ...runtime.previewData,
        studentUnitPrice: standardAllocation.studentUnitPrice,
        teacherUnitPrice: standardAllocation.teacherUnitPrice,
        fixedContractAmount: standardAllocation.unallocatedAmount,
        customerPaymentTotalAmount: standardFinancialSummary.customerPaymentTotalAmount,
        agentCollectionTotalAmount: standardFinancialSummary.agentCollectionTotalAmount,
      }
    : runtime.previewData;
  const frozenQuoteMetrics = successfulPreviewTemplateType === "standard"
    && standardAllocation
    && standardFinancialSummary
    ? {
        customerPaymentTotalAmount: standardFinancialSummary.customerPaymentTotalAmount,
        internalCostTotalAmount: standardFinancialSummary.internalCostTotalAmount,
        agentCollectionTotalAmount: standardFinancialSummary.agentCollectionTotalAmount,
        receivableTotalAmount: standardFinancialSummary.allocatedContractTotalAmount,
        grossProfitAmount: standardFinancialSummary.expectedGrossProfitAmount,
        marginRate: standardFinancialSummary.expectedMarginRate,
        unitPrice: standardAllocation.studentUnitPrice,
      }
    : {
        customerPaymentTotalAmount: summary.customerPaymentTotalAmount,
        internalCostTotalAmount: summary.internalCostTotalAmount,
        agentCollectionTotalAmount: summary.agentCollectionTotalAmount,
        receivableTotalAmount: summary.contractReceivableAmount,
        grossProfitAmount: summary.expectedGrossProfitAmount,
        marginRate: summary.expectedMarginRate,
        unitPrice: roleSummary.studentUnitPrice,
      };
  const salesSummary = feeAdjustmentContext && feeAdjustmentsQuery.data
    ? buildFeeAdjustmentQuoteSummary(frozenQuoteMetrics, feeAdjustmentsQuery.data)
    : undefined;
  const quoteMetrics = { ...frozenQuoteMetrics, ...salesSummary };
  const headerMetrics = calculateCustomerQuoteHeaderMetrics(snapshot, quoteMetrics.unitPrice);
  const originalHeaderMetrics = calculateCustomerQuoteHeaderMetrics(snapshot, roleSummary.studentUnitPrice);
  const headerPerStudentUnavailable = isRosterPricingLocked ? "冻结口径不可用" : snapshot.studentCount <= 0 ? "未设置学生人数" : null;
  const targetEditingDisabled = editingDisabled || isRosterPricingLocked || standardAllocationPending
    || successfulPreviewTemplateType !== "standard" || !standardAllocation || Boolean(runtime.standardAllocationError);
  const feedbackMarginRate = quoteMetrics.marginRate;
  const markupPoints = markupPointDifference(feedbackMarginRate);
  const markupAchieved = Number(feedbackMarginRate) >= expectedTargetMarkupRate;
  const adjustmentAmountsUnavailable = Boolean(feeAdjustmentContext && !salesSummary);
  const currentMetricsState = feeAdjustmentsQuery.isError
    ? "加载失败"
    : feeAdjustmentsQuery.isLoading ? "加载中" : adjustmentAmountsUnavailable ? "金额数据不完整" : null;
  const quoteMetricsState = currentMetricsState ?? (standardAllocationPending ? "试算中" : null);
  const feedbackUnavailable = quoteMetricsState ?? (
    salesSummary && compareCustomerQuoteMoney(quoteMetrics.internalCostTotalAmount) === 0
      ? "当前成本为零，无法计算加价率"
      : null
  );

  /** 复制服务端生成的客户安全 Markdown。 */
  async function copyMarkdown() {
    if (copyMarkdownInFlightRef.current) return;
    copyMarkdownInFlightRef.current = true;
    setCopyingCustomerQuote(true);
    const requestId = pageActionRequestRef.current + 1;
    pageActionRequestRef.current = requestId;
    const isCurrentRequest = () => pageActionRequestRef.current === requestId;
    try {
      const markdown = await runtime.exportMarkdown(successfulPreviewTemplateType);
      if (!isCurrentRequest()) return;
      await writeClipboardText(markdown);
      if (!isCurrentRequest()) return;
      Message.success("客户报价已复制");
    } catch (error) {
      if (!isCurrentRequest()) return;
      Message.error(`复制失败：${customerQuoteActionErrorText(
        error,
        "客户报价操作暂不可用，请稍后重试。",
        editor.sourceBlockReason,
        editor.sourceState,
      )}`);
    } finally {
      copyMarkdownInFlightRef.current = false;
      setCopyingCustomerQuote(false);
    }
  }

  /** 高住宿加价确认前只保留展示草稿，确认后再统一生成最新预览。 */
  function changeGroupDisplayMode(
    lineNos: number[],
    displayMode: CustomerQuoteLine["displayMode"],
  ) {
    const templateType = desiredPreviewTemplateRef.current ?? activePreviewTemplateType;
    if (previewBlockedByStayMarkup) {
      void runtime.setGroupDisplayMode(lineNos, displayMode, templateType, false)
        .catch(() => undefined);
      return;
    }
    skipNextAutoPreviewRef.current = true;
    const revision = previewRevisionRef.current + 1;
    previewRevisionRef.current = revision;
    setPreviewStale(true);
    void runtime.setGroupDisplayMode(lineNos, displayMode, templateType)
      .then(async () => {
        if (previewRevisionRef.current !== revision) return;
        commitPendingTemplateSwitch(templateType);
        await syncStandardAllocation(templateType);
      })
      .catch(() => {
        if (previewRevisionRef.current !== revision) return;
        if (!rollbackPendingTemplateSwitch(templateType)) {
          const rollbackTemplateType = runtime.previewData?.templateType
            ?? snapshot.templateType;
          desiredPreviewTemplateRef.current = rollbackTemplateType;
          setDesiredPreviewTemplateType(rollbackTemplateType);
        }
      })
      .finally(() => {
        if (previewRevisionRef.current === revision) setPreviewStale(false);
      });
  }

  /** 将模板示例追加到清单，已有值保持唯一。 */
  function appendTemplateItem(
    items: string[],
    item: string,
    commit: (nextItems: string[]) => void,
  ) {
    commit(items.includes(item) ? items : [...items, item]);
  }

  /** 所有确认集中到独立区域，明细位置只负责定位。 */
  function sourceChangeConfirmButton(changeId: string) {
    return <Button size="small" onClick={() => {
      const target = document.getElementById(`customer-quote-pending-change-${changeId}`);
      target?.scrollIntoView?.({ block: "center", behavior: "smooth" });
      target?.focus({ preventScroll: true });
    }}>核对并设置加价</Button>;
  }

  /** 更新指定报价行的可编辑字段。 */
  function updateLine(line: CustomerQuoteLine, patch: Partial<CustomerQuoteLine>) {
    runtime.updateLine(line.lineNo, patch);
  }

  /** 更新或移除指定输入字段的本地错误反馈。 */
  function updateFieldError(field: string, message: string | null) {
    setFieldErrors((current) => {
      if (message === null) {
        if (!(field in current)) return current;
        const next = { ...current };
        delete next[field];
        return next;
      }
      return current[field] === message ? current : { ...current, [field]: message };
    });
  }

  /** 在 Arco 归一化前校验金额原始文本。 */
  function handleRawAmountInput(field: string, rawValue: string, min?: number) {
    const invalid = normalizeQuoteAmount(rawValue, min) === null;
    rawInputInvalidRef.current[field] = invalid;
    updateFieldError(field, invalid ? moneyInputErrorMessage : null);
  }

  /** 完成逐项金额编辑；非法或越界值恢复当前快照金额。 */
  function finishLineAmountEdit(line: CustomerQuoteLine, min: number | undefined) {
    const field = `line-amount-${line.lineNo}`;
    const draft = lineAmountDrafts[line.lineNo] ?? line.quoteAmount;
    const normalized = normalizeQuoteAmount(draft, min);
    if (rawInputInvalidRef.current[field] || normalized === null) {
      updateFieldError(field, moneyInputErrorMessage);
      rawInputInvalidRef.current[field] = false;
      setLineAmountDrafts((current) => ({ ...current, [line.lineNo]: line.quoteAmount }));
      setLineAmountRevisions((current) => ({
        ...current,
        [line.lineNo]: (current[line.lineNo] ?? 0) + 1,
      }));
      return;
    }
    updateFieldError(field, null);
    setLineAmountDrafts((current) => ({ ...current, [line.lineNo]: normalized }));
    if (lineAmountLastCommittedRef.current.get(line.lineNo) !== normalized) {
      lineAmountLastCommittedRef.current.set(line.lineNo, normalized);
      updateLine(line, { quoteAmount: normalized });
    }
  }

  /** 取消逐项金额编辑，并在快照回写前同步结束活动编辑态。 */
  function cancelLineAmountEdit(line: CustomerQuoteLine) {
    const initialValue = lineAmountInitialValuesRef.current.get(line.lineNo)
      ?? line.quoteAmount;
    const normalizedInitialValue = normalizeQuoteAmount(
      initialValue,
      line.lineOrigin === "budget" ? 0 : undefined,
    ) ?? initialValue;
    const field = `line-amount-${line.lineNo}`;
    focusedAmountLineRef.current = null;
    rawInputInvalidRef.current[field] = false;
    updateFieldError(field, null);
    lineAmountDraftsRef.current = {
      ...lineAmountDraftsRef.current,
      [line.lineNo]: normalizedInitialValue,
    };
    setLineAmountDrafts((current) => ({
      ...current,
      [line.lineNo]: normalizedInitialValue,
    }));
    setLineAmountRevisions((current) => ({
      ...current,
      [line.lineNo]: (current[line.lineNo] ?? 0) + 1,
    }));
    if (lineAmountLastCommittedRef.current.get(line.lineNo) !== normalizedInitialValue) {
      lineAmountLastCommittedRef.current.set(line.lineNo, normalizedInitialValue);
      updateLine(line, { quoteAmount: normalizedInitialValue });
    }
  }

  /** 归一化组级基础加价并校验全部成员的报价单价和金额。 */
  function normalizePricingGroupMarkup(
    pricingGroup: CustomerQuotePricingGroup,
    value: string,
  ): string | null {
    const normalized = normalizeCustomerQuoteMoney(value);
    if (normalized === null || !runtime.snapshot) return null;
    try {
      applyCustomerQuotePricingGroupMarkup(
        runtime.snapshot,
        pricingGroup.lineNos,
        normalized,
      );
      return normalized;
    } catch {
      return null;
    }
  }

  /** 把基础加价提交到单行或同名组运行时。 */
  function commitPricingGroupMarkup(
    pricingGroup: CustomerQuotePricingGroup,
    normalized: string,
  ) {
    if (
      pricingGroup.lines.length === 1
      && pricingGroup.factorMode !== "ticket_teacher_double"
    ) {
      runtime.setLineUnitMarkup(pricingGroup.lineNos[0], normalized);
      return;
    }
    runtime.setPricingGroupMarkup(pricingGroup.lineNos, normalized);
  }

  /** 完成组级加价编辑；非法值恢复当前快照中的精确基础加价。 */
  function finishPricingGroupMarkupEdit(pricingGroup: CustomerQuotePricingGroup) {
    const lineNo = pricingGroup.lineNos[0];
    const field = `line-markup-${lineNo}`;
    const currentMarkup = customerQuotePricingGroupBaseMarkup(pricingGroup);
    const draft = lineMarkupDrafts[lineNo] ?? currentMarkup;
    const normalized = normalizePricingGroupMarkup(pricingGroup, draft);
    if (rawInputInvalidRef.current[field] || normalized === null) {
      updateFieldError(field, markupInputErrorMessage);
      rawInputInvalidRef.current[field] = false;
      setLineMarkupDrafts((current) => ({ ...current, [lineNo]: currentMarkup }));
      setLineMarkupRevisions((current) => ({
        ...current,
        [lineNo]: (current[lineNo] ?? 0) + 1,
      }));
      return;
    }
    updateFieldError(field, null);
    setLineMarkupDrafts((current) => ({ ...current, [lineNo]: normalized }));
    if (lineMarkupLastCommittedRef.current.get(lineNo) !== normalized) {
      lineMarkupLastCommittedRef.current.set(lineNo, normalized);
      commitPricingGroupMarkup(pricingGroup, normalized);
    }
  }

  /** 取消组级加价编辑，并在快照回写前同步结束活动编辑态。 */
  function cancelPricingGroupMarkupEdit(pricingGroup: CustomerQuotePricingGroup) {
    const lineNo = pricingGroup.lineNos[0];
    const field = `line-markup-${lineNo}`;
    const initialValue = lineMarkupInitialValuesRef.current.get(lineNo)
      ?? customerQuotePricingGroupBaseMarkup(pricingGroup);
    focusedMarkupLineRef.current = null;
    rawInputInvalidRef.current[field] = false;
    updateFieldError(field, null);
    lineMarkupDraftsRef.current = {
      ...lineMarkupDraftsRef.current,
      [lineNo]: initialValue,
    };
    setLineMarkupDrafts((current) => ({ ...current, [lineNo]: initialValue }));
    setLineMarkupRevisions((current) => ({
      ...current,
      [lineNo]: (current[lineNo] ?? 0) + 1,
    }));
    if (lineMarkupLastCommittedRef.current.get(lineNo) !== initialValue) {
      lineMarkupLastCommittedRef.current.set(lineNo, initialValue);
      commitPricingGroupMarkup(pricingGroup, initialValue);
    }
  }

  /** 切换模板并立即刷新常驻客户预览；只读版本仅临时预览，不改快照。 */
  async function previewTemplate(templateType: CustomerQuoteTemplateType) {
    const requestId = pageActionRequestRef.current + 1;
    pageActionRequestRef.current = requestId;
    const persistedTemplateBeforeSwitch = snapshot.templateType;
    const successfulTemplateBeforeSwitch = runtime.previewData?.templateType
      ?? persistedTemplateBeforeSwitch;
    const persistsTemplate = templateType !== snapshot.templateType
      && !runtime.readonly
      && editor.canEditQuote
      && canUpdate;
    if (persistsTemplate) {
      pendingTemplateSwitchRef.current = {
        customerQuoteId,
        actionRequestId: requestId,
        requestedTemplateType: templateType,
        successfulTemplateType: successfulTemplateBeforeSwitch,
        rollbackPersistedTemplate: () => runtime.setTemplate(persistedTemplateBeforeSwitch),
      };
      skipNextAutoPreviewRef.current = true;
      runtime.setTemplate(templateType);
    }
    try {
      await refreshPreview(templateType, undefined, successfulTemplateBeforeSwitch);
      if (pageActionRequestRef.current !== requestId) return;
      if (persistsTemplate) commitPendingTemplateSwitch(templateType);
    } catch (error) {
      if (pageActionRequestRef.current !== requestId) return;
      if (
        persistsTemplate
        && desiredPreviewTemplateRef.current === successfulTemplateBeforeSwitch
      ) {
        rollbackPendingTemplateSwitch(templateType);
      }
      throw error;
    }
    await syncStandardAllocation(templateType);
  }

  /** 标记冲突草稿基线，供最新服务端快照在自动预览前接管模板。 */
  async function loadLatestAfterConflict() {
    const baselineSnapshot = snapshot;
    pendingConflictReloadSnapshotRef.current = baselineSnapshot;
    try {
      await runtime.loadLatestAfterConflict();
    } catch (error) {
      if (pendingConflictReloadSnapshotRef.current === baselineSnapshot) {
        pendingConflictReloadSnapshotRef.current = null;
      }
      throw error;
    }
  }

  /** 回到页首并把键盘焦点恢复到页面标题。 */
  function backToPageTop() {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      ?? false;
    pageRef.current?.scrollIntoView?.({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
    pageTitleRef.current?.focus({ preventScroll: true });
  }

  return (
    <>
      {unsavedChangesGuard.blocker}
      <div className="customer-quote customer-quote--split" ref={pageRef}>
      <div className="customer-quote__sticky-shell">
        <header className="customer-quote__header">
        <div className="customer-quote__title">
          <h1 ref={pageTitleRef} tabIndex={-1}>行程报价</h1>
          <div className="customer-quote__version-title">
            <Tag color={editor.status === "finalized" ? "green" : "blue"}>
              {statusLabels[editor.status] ?? editor.status}
            </Tag>
            {renamingQuoteId === customerQuoteId || runtime.versionNameError ? (
              <div className="customer-quote__version-name-editor">
                <Input
                  aria-label="报价名称"
                  aria-invalid={Boolean(runtime.versionNameError)}
                  aria-describedby={runtime.versionNameError ? "customer-quote-version-name-error" : undefined}
                  value={runtime.versionName}
                  disabled={editingDisabled}
                  maxLength={100}
                  size="small"
                  autoFocus={renamingQuoteId === customerQuoteId}
                  onChange={(value) => { setRenamingQuoteId(customerQuoteId); runtime.setVersionName(value); }}
                  onBlur={() => { if (!runtime.versionNameError) setRenamingQuoteId(null); }}
                  onPressEnter={() => { if (!runtime.versionNameError) setRenamingQuoteId(null); }}
                />
                {runtime.versionNameError ? <span id="customer-quote-version-name-error" className="customer-quote__field-error" role="alert">{runtime.versionNameError}</span> : null}
              </div>
            ) : (
              <h2 title={runtime.versionName || editor.versionName}>
                {editingDisabled ? runtime.versionName || editor.versionName : (
                  <Button className="customer-quote__version-name-button" type="outline" size="mini" icon={<PencilLine size={12} aria-hidden="true" />} title="点击修改报价名称" onClick={() => setRenamingQuoteId(customerQuoteId)}>
                    <span>{runtime.versionName || editor.versionName}</span>
                  </Button>
                )}
              </h2>
            )}
          </div>
          {runtime.editor.itineraryTitle ? (
            <p className="customer-quote__itinerary-title" aria-label="报价关联行程" title={runtime.editor.itineraryTitle}>
              <span>行程：</span>
              <strong>{runtime.editor.itineraryTitle}</strong>
            </p>
          ) : null}

        </div>
        <div className="customer-quote__actions">

          {writeActionBlockedReason ? (
            <Tag color="arcoblue" role="status">{writeActionBlockedReason}</Tag>
          ) : null}
          {isDraft && canUpdate ? (
            <Button
              icon={<Save size={16} />}
              disabled={saveDisabled}
              loading={runtime.saving}
              title={runtime.versionNameError ?? writeActionBlockedReason}
              onClick={() => void runtime.save()
                .then(() => Message.success(`报价 V${editor.versionNo} 草稿已保存`))
                .catch(showActionFailure)}
            >
              保存草稿
            </Button>
          ) : null}
          {isDraft && canUpdate ? (
            <Popconfirm
              title={(
                <div className="customer-quote__finalize-confirm">
                  <strong>确认定稿当前报价？</strong>
                  <span>报价 V{editor.versionNo} · {successfulPreviewTemplateType === "standard" ? "正式金额（取整前）" : "客户支付"} {formatCustomerQuoteMoney(summary.customerPaymentTotalAmount)}</span>
                  {successfulPreviewTemplateType === "standard" && standardFinancialSummary ? <span>标准报价试算金额（取整后） {formatCustomerQuoteMoney(standardFinancialSummary.customerPaymentTotalAmount)}</span> : null}
                  <span>来源核价 V{snapshot.sourceBudgetVersionNo} · 定稿后不可原地修改</span>
                </div>
              )}
              onOk={() => runtime.finalize()
                .then(() => {
                  Message.success(`报价 V${editor.versionNo} 已定稿`);
                  if (canAccessBudget) navigate(`/quote/budgets/${snapshot.sourceBudgetOrderId}`);
                })
                .catch(showActionFailure)}
              disabled={finalizeDisabled}
              autoFocus
              focusLock
            >
              <Button
                type="primary"
                icon={<CheckCircle2 size={16} />}
                disabled={finalizeDisabled}
                loading={runtime.finalizing}
                title={finalizeBlockedReason}
              >
                定稿
              </Button>
            </Popconfirm>
          ) : null}
        </div>
        </header>
      {visibleActionError ? (
        <div ref={actionErrorRef} tabIndex={-1} className="customer-quote__action-error"><Alert
          type="error"
          showIcon
          content={customerQuoteActionErrorText(
            visibleActionError,
            "客户报价操作暂不可用，请稍后重试。",
            editor.sourceBlockReason,
            editor.sourceState,
          )}
        /></div>
      ) : null}


      </div>

      <div className="customer-quote__meta-row">
          <div className="customer-quote__version-meta">

            <span title={`来源核价 V${snapshot.sourceBudgetVersionNo}（${snapshot.sourceBudgetVersionName}）`}>来源核价 V{snapshot.sourceBudgetVersionNo}</span>
            {runtime.isDirty ? <b>未保存</b> : null}
          {isDraft && canUpdate ? <Popconfirm
            title="确认放弃本页所有未保存修改，恢复到已保存草稿？"
            onOk={() => (async () => {
              setRestoringSavedDraft(true);
              pendingConflictReloadSnapshotRef.current = runtime.snapshot;
              try {
                await runtime.restoreSavedDraft();
                rawInputInvalidRef.current = {};
                setFieldErrors({});
                Message.success("已恢复保存的草稿");
              } finally {
                setRestoringSavedDraft(false);
              }
            })().catch(showActionFailure)} autoFocus focusLock
          ><Button disabled={!runtime.isDirty || actionBusy || Boolean(runtime.writeConflict)} loading={restoringSavedDraft}>恢复已保存草稿</Button></Popconfirm> : null}
          </div>
      <div className="customer-quote__people-basis" aria-label="报价人数口径" title="当前报价版本参与计算的人数">
        <span>人数口径：</span>
        <span>学生 <strong>{snapshot.studentCount}</strong> 人</span>
        <span>老师 <strong>{snapshot.teacherCount}</strong> 人</span>
        <span>导游 <strong>{snapshot.guideCount}</strong> 人</span>
        <span>司机 <strong>{snapshot.driverCount}</strong> 人</span>
      </div>
      <nav className="customer-quote__related-links" aria-label="行程报价关联业务快速入口">
        <strong>关联业务</strong>
        <div>
          <a
            href={`/itineraries/${encodeURIComponent(editor.itineraryId)}/preview`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Eye size={15} aria-hidden="true" />
            行程预览
          </a>
          {canAccessBudget ? <a
            href={`/quote/customer-quotes/${encodeURIComponent(customerQuoteId)}/budget`}
            target="_blank"
            rel="noopener noreferrer"
          ><Calculator size={15} aria-hidden="true" />行程核价</a> : null}
          {canAccessRoster && editor.projectId ? <a
            href={`/crm/order-handover/${encodeURIComponent(editor.projectId)}`}
            target="_blank"
            rel="noopener noreferrer"
          ><Users size={15} aria-hidden="true" />人员名单</a> : null}
          {canReturnBudget && isDraft && !runtime.readonly ? <CustomerQuoteBudgetReturnAction key={customerQuoteId}
            customerQuoteId={customerQuoteId} itineraryTitle={editor.itineraryTitle}
            sourceBudgetOrderId={canAccessBudget ? editor.budgetOrderId : undefined}
            disabled={writeActionBusy} isDirty={runtime.isDirty}
            onBusyChange={setReturningBudget} onCompleted={() => runtime.reload()}
            prepare={async () => {
              const saved = runtime.isDirty ? await runtime.save() : editor;
              return { quoteRevision: saved.quoteRevision, sourceRevision: saved.sourceRevision };
            }} /> : null}
        </div>
      </nav>
      </div>



      {runtime.writeConflict ? (
        <Alert
          type="error"
          showIcon
          content={(
            <div className="customer-quote__source-summary">
              <strong>报价版本已发生冲突</strong>
              <span>当前未保存修改仍保留。加载最新版本会放弃这些修改，并以服务端最新内容重新开始。</span>
              {runtime.conflictReloadError ? <span>最新版本加载失败，请稍后重试。</span> : null}
              <div className="customer-quote__recovery-actions">
                <Button
                  type="primary"
                  loading={runtime.loadingLatestAfterConflict}
                  disabled={actionBusy || runtime.previewing}
                  onClick={() => void loadLatestAfterConflict().catch(() => undefined)}
                >
                  加载最新版本
                </Button>
              </div>
            </div>
          )}
        />
      ) : null}
      {runtime.readonly && isDraft && editor.sourceBlockReason ? (
        <Alert type="info" showIcon content={editor.sourceBlockReason} />
      ) : null}
      {sourceChanges.length ? (
        <CustomerQuoteSourceChanges key={customerQuoteId} snapshot={snapshot} changes={sourceChanges} canConfirm={canConfirmSourceChange}
          busy={otherActionBusy} isDirty={runtime.isDirty} blockedReason={canConfirmSourceChange ? undefined : writeActionBlockedReason}
          onLocate={locateSourceChange} onConfirm={(changeId, baseMarkup, financeTreatment) => runtime.confirmSourceChange(changeId, false, baseMarkup, financeTreatment)} />
      ) : null}
      {editor.sourceState !== "finalized" ? (
        <Alert
          type={editor.sourceState === "editing" ? "info" : "warning"}
          showIcon
          content={editor.sourceState === "editing" || editor.sourceState === "unavailable" ? (
            <div className="customer-quote__source-summary">
              <strong>{editor.sourceState === "editing" ? "核价正在编辑" : "来源核价暂不可用"}</strong>
              <span>{editor.sourceState === "editing" ? "核价正在编辑，当前报价沿用已确认数据，可继续编辑和保存；重新定稿后才出现新变化。" : editor.sourceBlockReason || "来源核价当前不可用，请稍后刷新。"}</span>
              {runtime.reloadError ? <span>重新检查失败，请稍后重试。</span> : null}
              <div className="customer-quote__recovery-actions">
                <Button
                  type="primary"
                  loading={runtime.reloading}
                  disabled={runtime.reloading || actionBusy || runtime.previewing}
                  onClick={() => void runtime.reload().catch(() => undefined)}
                >
                  重新检查来源
                </Button>
                {canAccessBudget ? <a href={`/quote/customer-quotes/${encodeURIComponent(customerQuoteId)}/budget`} target="_blank" rel="noopener noreferrer">返回来源核价</a> : null}
              </div>
            </div>
          ) : editor.sourceBlockReason || "来源核价当前不可用，请稍后刷新。"}
        />
      ) : null}
      {isDraft && finalizeErrors.length ? (
        <Alert
          type="error"
          showIcon
          content={(
            <div>
              <strong>当前报价尚不能定稿</strong>
              {finalizeErrors.map((message, index) => <p key={`${message}:${index}`}>{message}</p>)}
            </div>
          )}
        />
      ) : null}
      {isDraft && requiresStayHighMarkupConfirmation ? (
        <Alert
          type="warning"
          showIcon
          content={(
            <div className="customer-quote__high-stay-confirmation">
              <strong>住宿每人晚加价达到 ¥15</strong>
              <span>请确认该加价已人工核实；确认后请先保存草稿，未确认或未保存均不能定稿。</span>
              <Switch
                aria-label="确认住宿高加价"
                checked={Boolean(snapshot.stayHighMarkupConfirmed)}
                disabled={editingDisabled}
                onChange={(checked) => runtime.setStayHighMarkupConfirmed(checked)}
              />
            </div>
          )}
        />
      ) : null}

      <div className="customer-quote__content">
        <aside className="customer-quote__sidebar" aria-label="报价汇总与模板">
          <section className="customer-quote__total-card">
            <button type="button" className="customer-quote__unit-total" title={isRosterPricingLocked ? "冻结合同人均" : "按学生承担规则计算，含代收代付"} onClick={openCustomerPreview}>
              <span>{isRosterPricingLocked ? "冻结人均单价" : "对客人均单价"}</span><strong>{standardAllocationPending ? "试算中" : isRosterPricingLocked ? formatCustomerQuoteMoney(quoteMetrics.unitPrice, true) : headerPerStudentUnavailable ?? formatCustomerQuoteMoney(headerMetrics.customerPerStudent!, true)}</strong>
            </button>
            <div className="customer-quote__total-facts">
              <div title="按学生承担规则计算，含代收代付"><span>核价人均</span><strong>{headerPerStudentUnavailable ?? formatCustomerQuoteMoney(headerMetrics.costPerStudent!)}</strong></div>
              <div className="customer-quote__profit-fact"><span>人均毛利</span><Button className="customer-quote__editable-metric" type="outline" size="mini" icon={<PencilLine size={12} aria-hidden="true" />} aria-label="修改人均毛利" title="点击修改人均毛利并重新计算" disabled={targetEditingDisabled || Boolean(headerPerStudentUnavailable)}
                onClick={() => { if (headerMetrics.differencePerStudent !== null) setTargetInput({ kind: "per_student_difference", amount: headerMetrics.differencePerStudent }); }}>
                <strong>{headerPerStudentUnavailable ?? quoteMetricsState ?? formatCustomerQuoteMoney(headerMetrics.differencePerStudent!)}</strong>
              </Button></div>
              <div className="customer-quote__profit-fact"><span>报价毛利</span><strong>{quoteMetricsState ?? formatCustomerQuoteWholeYuan(quoteMetrics.grossProfitAmount)}</strong></div>
              <div><span>报价毛利率</span><Button className="customer-quote__editable-metric" type="outline" size="mini" icon={<PencilLine size={12} aria-hidden="true" />} aria-label="修改报价毛利率" title="点击修改毛利率并重新计算，按毛利÷核价成本计算" disabled={targetEditingDisabled || compareCustomerQuoteMoney(quoteMetrics.internalCostTotalAmount) <= 0}
                onClick={() => setTargetInput({ kind: "margin_rate", amount: formatCustomerQuoteMarginRatePercent(quoteMetrics.marginRate).replace("%", "") })}>
                <strong>{quoteMetricsState ?? formatCustomerQuoteMarginRatePercent(quoteMetrics.marginRate)}</strong>
              </Button></div>
              <div><span>核价成本</span><strong>{currentMetricsState ?? formatCustomerQuoteMoney(quoteMetrics.internalCostTotalAmount, true)}</strong></div>
              <div aria-label="预览报价总价"><span>对客报价总价</span><strong>{quoteMetricsState ?? formatCustomerQuoteMoney(quoteMetrics.customerPaymentTotalAmount, true)}</strong></div>
            </div>
          </section>
          <section className="customer-quote__summary-card" aria-label="分组报价导航">
            <div className="customer-quote__summary-heading">
              <h2 id="quote-summary-heading">对客分组报价</h2>
              <small>{groups.length} 个大项</small>
            </div>
            <div aria-labelledby="quote-summary-heading" className="customer-quote__summary-scroll" tabIndex={0}>
              <table className="customer-quote__summary-table">
                <thead><tr><th>对客大项</th><th>核价成本</th><th>对客报价</th><th>学生人均（全程）</th><th>毛利</th><th>毛利率</th></tr></thead>
                <tbody>{summaryGroups.map((group) => <tr key={group.key} className={selectedGroupKey === group.key ? "is-selected" : undefined}
                  onClick={() => { setSelectedGroupKey(group.key); setExpandedGroupKeys((current) => new Set([...(current ?? groups.map((item) => item.key)), group.key]));
                    requestAnimationFrame(() => document.getElementById(`quote-fee-${group.key}`)?.scrollIntoView?.({ block: "start", behavior: "smooth" })); }}>
                  <th><button type="button" aria-label={`定位${group.groupName}明细`}>{group.groupName}{group.includesAgent ? "（含代收代付）" : ""}</button></th>
                  <td>{formatCustomerQuoteMoney(group.costAmount, true)}</td><td>{groupAmountState ?? formatCustomerQuoteMoney(group.quoteAmount, true)}</td><td>{groupAmountState ?? (group.studentUnitPrice === null ? "—" : formatCustomerQuoteMoney(group.studentUnitPrice, true))}</td><td>{groupAmountState ?? formatCustomerQuoteWholeYuan(group.profitAmount)}</td>
                  <td>{groupAmountState ?? customerQuoteMarkupRatePercent(group) ?? "—"}</td>
                </tr>)}</tbody>
                <tfoot><tr><th>合计</th><td>{formatCustomerQuoteMoney(summary.internalCostTotalAmount, true)}</td><td>{groupAmountState ?? formatCustomerQuoteMoney(frozenQuoteMetrics.customerPaymentTotalAmount, true)}</td><td>{groupAmountState ?? (snapshot.studentCount <= 0 ? "—" : formatCustomerQuoteMoney(addCustomerQuoteAgentPerStudent(frozenQuoteMetrics.unitPrice, agentPerStudent)!, true))}</td><td>{groupAmountState ?? formatCustomerQuoteWholeYuan(frozenQuoteMetrics.grossProfitAmount)}</td><td>{groupAmountState ?? formatCustomerQuoteMarginRatePercent(frozenQuoteMetrics.marginRate)}</td></tr></tfoot>
              </table>
            </div>
            {hasAgentCollection ? <p className="customer-quote__basis-note" aria-label="学生人均收款口径">
              合同报价人均：{groupAmountState ?? (snapshot.studentCount <= 0 ? "—" : formatCustomerQuoteMoney(frozenQuoteMetrics.unitPrice))}；
              代收代付人均：{agentPerStudent === null ? "—" : formatCustomerQuoteMoney(agentPerStudent, true)}；
              含代收代付的支付人均：{groupAmountState ?? (agentPerStudent === null ? "—" : formatCustomerQuoteMoney(addCustomerQuoteAgentPerStudent(frozenQuoteMetrics.unitPrice, agentPerStudent)!, true))}。
            </p> : null}
            <p className="customer-quote__basis-note">{useRoundedSummary ? "合同内分组与人均使用取整后报价；代收代付按实际金额计入，人均保留两位，不增加取整补差。" : "当前展示取整前报价。"}合计含代收代付人均，不含合同固定金额。毛利按整元显示，精确值见计算明细。</p>
          </section>
        <section className="customer-quote__template-panel" aria-label="客户报价模板">
          <div className="customer-quote__template">
            <div><h2>对客报价模板</h2></div>
            <div className="customer-quote__template-cards customer-quote__template-cards--single" role="radiogroup" aria-label="客户报价模板">
              <label>
                <input type="radio" name="customer-quote-template" aria-label="标准报价"
                  checked={activePreviewTemplateType === "standard"} disabled={templatePreviewSwitchDisabled}
                  onChange={() => void previewTemplate("standard").catch(() => undefined)} />
                <span aria-hidden="true">▤</span><strong>标准报价</strong>
                <small>团队方案，整元试算</small>
              </label>
            </div>
            <p className="customer-quote__template-help">{activePreviewTemplateType === "transparent"
              ? "当前为历史透明报价，可选择标准报价进行预览。"
              : activePreviewTemplateType === "standard"
                ? "标准报价：按团队方案展示，并提供逐项整元试算；复制、Excel 与定稿仍按取整前正式金额。"
                : "当前为历史打包报价，可选择标准报价进行预览。"}
              {runtime.readonly ? " 当前为只读版本，切换仅影响本次预览。" : " 草稿切换模板需保存后生效。"}</p>
            {successfulPreviewTemplateType === "standard" && standardAllocation ? <p className="customer-quote__template-help">正式客户支付（取整前）：{formatCustomerQuoteMoney(summary.customerPaymentTotalAmount)}；标准取整差额：+{formatCustomerQuoteMoney(standardAllocation.upliftTotalAmount)}。费用调整另列于“报价、成本与利润”。</p> : null}
            <div className="customer-quote-preview__footer">
              <Button type="primary" icon={<Eye size={16} />} disabled={previewBlockedBySource} onClick={() => {
                openCustomerPreview();
                if (!previewActionsDisabled && !runtime.previewing && !runtime.calculatingStandardAllocation && (!runtime.previewIsCurrent || runtime.previewError || runtime.standardAllocationError)) {
                  void refreshPreview().then((result) => syncStandardAllocation(result.templateType)).catch(showActionFailure);
                }
              }}>客户预览</Button>
              {previewStale ? <Tag color="arcoblue" role="status" aria-live="polite">正在同步客户预览…</Tag> : null}
              {canExport ? <Button icon={<Download size={16} />} disabled={downloadDisabled} title={outputBlockedReason} loading={runtime.downloading} onClick={() => void runtime.downloadExcel(successfulPreviewTemplateType).catch(showActionFailure)}>下载 Excel</Button> : null}
              {canExport ? <Button type="primary" icon={<Copy size={16} />} disabled={downloadDisabled} title={outputBlockedReason} loading={runtime.exportingMarkdown || copyingCustomerQuote} onClick={() => void copyMarkdown()}>复制客户报价</Button> : null}
              {successfulPreviewTemplateType === "standard" ? <span>复制、Excel 和定稿均使用取整前正式金额</span> : null}
            </div>
          </div>
          <Modal title={targetInput?.kind === "per_student_difference" ? "按人均毛利重新计算" : "按报价毛利率重新计算"}
            visible={Boolean(targetInput)} onCancel={() => { if (!runtime.repricingTarget) setTargetInput(null); }}
            okText="重新计算" confirmLoading={runtime.repricingTarget}
            okButtonProps={{ disabled: targetEditingDisabled || !targetInput || !/^\d{1,16}(\.\d{1,2})?$/.test(targetInput.amount) }}
            onOk={async () => {
              if (!targetInput || targetEditingDisabled) return;
              const requestId = ++pageActionRequestRef.current;
              try {
                await runtime.repriceTarget(targetInput.kind, targetInput.amount);
                if (pageActionRequestRef.current !== requestId) return;
                setTargetInput(null);
                Message.success("已按目标重新分配加价，请核对取整结果后保存草稿");
              } catch (error) {
                if (pageActionRequestRef.current === requestId) showActionFailure(error);
              }
            }} autoFocus focusLock>
            <Input aria-label={targetInput?.kind === "margin_rate" ? "目标毛利率" : "目标金额"} inputMode="decimal" prefix={targetInput?.kind === "margin_rate" ? undefined : "¥"} suffix={targetInput?.kind === "margin_rate" ? "%" : undefined} value={targetInput?.amount ?? ""}
              disabled={runtime.repricingTarget} onChange={(amount) => setTargetInput((current) => current ? { ...current, amount } : null)} />
            <p>重新分配用车、住宿、服务保障的加价；其他项目保持当前报价。</p>
            {targetInput?.kind === "margin_rate" ? <p>报价毛利率＝报价毛利 ÷ 核价成本；输入 18 表示目标 18%。</p> : null}
            <p>对客人均向上取整，实际结果可能略高于目标。计算结果需保存草稿后生效。</p>
          </Modal>
          <Modal title="报价计算明细" visible={calculationOpen} onCancel={() => setCalculationOpen(false)} footer={null} style={{ width: "min(860px, 94vw)" }} autoFocus focusLock>
            {currentMetricsState ? <p>费用调整{currentMetricsState}，当前报价目标暂不可判定。</p> : standardAllocationPending ? <p>标准报价试算中</p> : salesSummary ? <>
              <p>当前毛利（{formatCustomerQuoteMoney(quoteMetrics.grossProfitAmount)}） = 当前客户支付总额（{formatCustomerQuoteMoney(quoteMetrics.customerPaymentTotalAmount)}） - 当前成本（{formatCustomerQuoteMoney(quoteMetrics.internalCostTotalAmount)}）</p>
              <p>当前加价率 = 当前毛利（{formatCustomerQuoteMoney(quoteMetrics.grossProfitAmount)}） / 当前成本（{formatCustomerQuoteMoney(quoteMetrics.internalCostTotalAmount)}）</p>
            </> : <>
              <p>预计毛利（{formatCustomerQuoteMoney(frozenQuoteMetrics.grossProfitAmount)}） = 客户支付（{formatCustomerQuoteMoney(frozenQuoteMetrics.customerPaymentTotalAmount)}） - 报价成本（{formatCustomerQuoteMoney(frozenQuoteMetrics.internalCostTotalAmount)}）</p>
              <p>预计加价率 = 预计毛利（{formatCustomerQuoteMoney(frozenQuoteMetrics.grossProfitAmount)}） / 报价成本（{formatCustomerQuoteMoney(frozenQuoteMetrics.internalCostTotalAmount)}）</p>
              {hasAgentCollection ? <p>代收代付金额：{formatCustomerQuoteMoney(summary.agentCollectionTotalAmount)}，按实际金额计入，不增加人均取整补差。</p> : null}
            </>}
          {successfulPreviewTemplateType === "standard" ? (
            !canAutomaticallyCalculate ? <Alert
              type="info"
              showIcon
              content={currentUserQuery.isLoading
                ? "正在确认修改权限，暂未自动试算；当前展示取整前报价。"
                : currentUserQuery.isError
                  ? "修改权限暂不可用，未自动试算；当前展示取整前报价。"
                  : "无修改权限，未自动试算；当前展示取整前报价。"}
            /> :
            <StandardQuoteAllocationPanel
              allocation={standardAllocation}
              financialSummary={standardFinancialSummary}
              loading={runtime.calculatingStandardAllocation}
              errorText={standardAllocationErrorText}
            />
          ) : null}
          </Modal>
          <Modal className={`customer-quote customer-quote__dialog${displayedCustomerPreview?.templateType === "standard" && standardAllocation ? " customer-quote-fee-dialog" : ""}`} title={displayedCustomerPreview?.templateType === "standard" && standardAllocation ? null : "客户报价预览"} visible={previewOpen} onCancel={() => setPreviewOpen(false)} footer={null} style={{ width: "min(960px, calc(100vw - 32px))" }} autoFocus focusLock>
          {snapshot.templateType === "package" ? <div className="customer-quote__package">
            <div className="customer-quote__package-field">
              <label>费用包含<Input.TextArea aria-label="费用包含" value={runtime.includedItemsText} disabled={editingDisabled} autoSize={{ minRows: 2, maxRows: 5 }} onChange={runtime.setIncludedItemsText} onBlur={runtime.normalizeIncludedItemsText} /></label>
              <div className="customer-quote__template-suggestions">
                {includedItemSuggestions.map((item) => <Button key={item} size="mini" icon={<Plus size={13} />} disabled={editingDisabled} aria-label={`添加费用包含示例：${item}`} onClick={() => appendTemplateItem(snapshot.includedItems, item, runtime.setIncludedItems)}>{item}</Button>)}
              </div>
            </div>
            <div className="customer-quote__package-field">
              <label>费用不含<Input.TextArea aria-label="费用不含" value={runtime.excludedItemsText} disabled={editingDisabled} autoSize={{ minRows: 2, maxRows: 5 }} onChange={runtime.setExcludedItemsText} onBlur={runtime.normalizeExcludedItemsText} /></label>
              <div className="customer-quote__template-suggestions">
                {excludedItemSuggestions.map((item) => <Button key={item} size="mini" icon={<Plus size={13} />} disabled={editingDisabled} aria-label={`添加费用不含示例：${item}`} onClick={() => appendTemplateItem(snapshot.excludedItems, item, runtime.setExcludedItems)}>{item}</Button>)}
              </div>
            </div>
          </div> : null}
          <div className="customer-quote__preview-card" role="region" aria-label="客户报价预览">
            {!runtime.previewIsCurrent && !runtime.previewing && !previewStale && !previewBlockedBySource ? (
              <Alert type="warning" showIcon content={runtime.previewData
                ? "下方保留上次成功的预览供参考。当前草稿尚未同步，请点击“客户预览”同步后再复制或下载。"
                : "请先成功生成客户预览，再复制或下载。"} />
            ) : null}
            {sourceChanges.length ? (
              <Alert
                type="warning"
                showIcon
                content={`预览尚未包含 ${sourceChanges.length} 项最新核价变化`}
              />
            ) : null}
            {displayedCustomerPreview && !customerOutputBlockedByStayMarkup ? (
              <CustomerPreviewPanel
                preview={displayedCustomerPreview}
                customerPerStudent={headerMetrics.customerPerStudent}
                versionLabel={`对客报价 V${editor.versionNo}`}
                pointSourceLines={snapshot.lines}
                unroundedPaymentTotalAmount={runtime.previewData?.customerPaymentTotalAmount}
                standardAllocation={standardAllocation}
                standardAllocationPending={standardAllocationPending}
                standardFieldVisibility={standardFieldVisibility}
                onToggleStandardField={(field) => setStandardFieldVisibility((current) => ({
                  ...current,
                  [field]: !current[field],
                }))}
              />
            ) : <div className="customer-quote__preview-empty">{previewBlockedBySource ? "来源恢复后可点击“客户预览”重新生成。" : customerOutputBlockedByStayMarkup ? "请先确认住宿高加价后再生成客户报价。" : runtime.previewing ? "正在生成客户安全预览…" : "点击“客户预览”生成当前模板。"}</div>}
            {runtime.previewError && !previewBlockedBySource ? <Alert type="error" content={customerQuoteActionErrorText(
              runtime.previewError,
              "客户报价预览失败，请稍后重试。",
              editor.sourceBlockReason,
              editor.sourceState,
            )} /> : null}
          </div>
          </Modal>
        </section>

        </aside>
        <div className="customer-quote__main">
          <section className="customer-quote__comparison" aria-label="报价口径对照">
        <div className="customer-quote__comparison-scroll">
          <table className="customer-quote__comparison-table" aria-label="报价摘要">
            <thead><tr><th>报价口径</th><th>核价成本</th><th>代收代付</th><th>合同应收总额</th><th>报价毛利</th><th>报价毛利率</th><th>{isRosterPricingLocked ? "冻结人均单价" : "对客人均（含代收）"}</th></tr></thead>
            <tbody>
              <tr className="is-featured"><th>{salesSummary ? "当前合计（调整后）" : successfulPreviewTemplateType === "standard" && standardAllocation ? "标准整元试算" : "当前报价"}</th><td>{currentMetricsState ?? formatCustomerQuoteMoney(quoteMetrics.internalCostTotalAmount)}</td><td>{quoteMetricsState ?? formatCustomerQuoteMoney(quoteMetrics.agentCollectionTotalAmount, true)}</td><td>{quoteMetricsState ?? formatCustomerQuoteMoney(quoteMetrics.receivableTotalAmount, true)}</td><td>{quoteMetricsState ?? formatCustomerQuoteWholeYuan(quoteMetrics.grossProfitAmount)}</td><td>{quoteMetricsState ?? formatCustomerQuoteMarginRatePercent(quoteMetrics.marginRate)}</td><td>{standardAllocationPending ? "试算中" : isRosterPricingLocked ? formatCustomerQuoteMoney(quoteMetrics.unitPrice, true) : headerPerStudentUnavailable ?? formatCustomerQuoteMoney(headerMetrics.customerPerStudent!, true)}</td></tr>
              <tr><th>取整前正式报价</th><td>{formatCustomerQuoteMoney(summary.internalCostTotalAmount)}</td><td>{formatCustomerQuoteMoney(summary.agentCollectionTotalAmount)}</td><td>{formatCustomerQuoteMoney(summary.contractReceivableAmount)}</td><td>{formatCustomerQuoteMoney(summary.expectedGrossProfitAmount)}</td><td>{formatCustomerQuoteMarginRatePercent(summary.expectedMarginRate)}</td><td>{isRosterPricingLocked ? formatCustomerQuoteMoney(roleSummary.studentUnitPrice) : originalHeaderMetrics.customerPerStudent === null ? "—" : formatCustomerQuoteMoney(originalHeaderMetrics.customerPerStudent)}</td></tr>
            </tbody>
          </table>
        </div>
        <div className={`customer-quote__margin-feedback ${markupAchieved ? "is-achieved" : "is-gap"}`}>
          <div className="customer-quote__target-status">
            <span className="customer-quote__target-dot" aria-hidden="true">●</span>
            <strong>{salesSummary || feeAdjustmentContext ? "当前报价目标 16%（含费用调整）" : successfulPreviewTemplateType === "standard" && standardAllocation && standardFinancialSummary ? "标准报价目标 16%（含整元取整）" : "报价目标 16%（取整前）"}</strong>
            <span className="customer-quote__target-result">{feedbackUnavailable ?? (markupAchieved ? "正常，已达 16%" : "仍低于预期")}{!feedbackUnavailable ? ` · 较加价目标 ${markupPoints} 个百分点` : ""}</span>
          </div>
          <div className="customer-quote__target-formula">
            <span title={`毛利 = 客户支付总额 − 报价成本；加价率 = 毛利 ÷ 报价成本。${successfulPreviewTemplateType === "standard" ? "整元结果仅用于试算，正式输出仍使用取整前金额。" : ""}`}>毛利率 = 毛利 ÷ 报价成本</span>
            <Button type="text" size="mini" onClick={() => setCalculationOpen(true)}>计算明细</Button>
          </div>
        </div>
          </section>

          {headerSourceChanges.length ? (
            <div className="customer-quote__header-source-changes" aria-label="核价参数变化">
              {headerSourceChanges.map((change) => (
                <div key={change.changeId} id={`customer-quote-change-${change.changeId}`} tabIndex={-1}>
                  <strong>{change.itemName}：{change.oldValue ?? "无"} → {change.newValue ?? "无"}</strong>
                  {sourceChangeConfirmButton(change.changeId)}
                </div>
              ))}
            </div>
          ) : null}


        {feeAdjustmentContext || feeAdjustmentsQuery.isLoading || feeAdjustmentsQuery.isError ? <CustomerQuoteSalesSummary
          summary={salesSummary}
          agentCollectionAmount={frozenQuoteMetrics.agentCollectionTotalAmount}
          isLoading={feeAdjustmentsQuery.isLoading || standardAllocationPending}
          onRetry={feeAdjustmentsQuery.retry}
          isRetrying={feeAdjustmentsQuery.isRetrying}
          errorMessage={feeAdjustmentsQuery.isError ? "费用调整加载失败，请稍后重试。" : !feeAdjustmentsQuery.isLoading && adjustmentAmountsUnavailable ? "金额数据不完整，暂无法计算当前总额，请刷新后重试。" : null}
        /> : null}

        <section className="customer-quote__groups" aria-label="对客分组报价">
          <div className="customer-quote__section-head">
            <div><h2>费用明细 <small>{groups.length} 个分组 · {snapshot.lines.length} 条费用</small></h2></div><div><Button type="text" size="mini" onClick={() => setExpandedGroupKeys(new Set(groups.map((group) => group.key)))}>展开全部</Button><Button type="text" size="mini" onClick={() => setExpandedGroupKeys(new Set())}>收起全部</Button></div>
          </div>
          {addedSourceChanges.length ? (
            <div className="customer-quote__added-source-changes" aria-label="新增核价费用">
              {Object.entries(addedSourceChangeGroups).map(([category, changes]) => (
                <section key={category}>
                  <h3>{feeCategoryLabels[category as CustomerQuoteLine["feeCategory"]]} · {changes.length} 项新增</h3>
                  {changes.map((change) => (
                    <div key={change.changeId} id={`customer-quote-change-${change.changeId}`} tabIndex={-1}>
                      <strong>新增待确认：{change.itemName}</strong>
                      <span>原核价成本：无</span>
                      <span>当前核价成本：{change.newCostAmount === null ? "无" : formatCustomerQuoteMoney(change.newCostAmount)}</span>
                      <span>成本差额：{formatSourceCostDelta(null, change.newCostAmount)}</span>
                      <span>当前对客报价：待加入</span>
                      {sourceChangeConfirmButton(change.changeId)}
                    </div>
                  ))}
                </section>
              ))}
            </div>
          ) : null}
          {hiddenTaxSourceChanges.length ? (
            <div className="customer-quote__added-source-changes" aria-label="税费核价变化">
              <section>
                <h3>税费 · {hiddenTaxSourceChanges.length} 项待确认</h3>
                {hiddenTaxSourceChanges.map((change) => (
                  <SourceChangeWarning
                    key={change.changeId}
                    change={change}
                    confirming={runtime.confirmingSourceChange}
                    canConfirm={canConfirmSourceChange}
                    blockedReason={writeActionBlockedReason}
                    isDirty={runtime.isDirty}
                    onConfirm={(...args) => runtime.confirmSourceChange(...args).catch((error: unknown) => { showActionFailure(error); throw error; })}
                  />
                ))}
              </section>
            </div>
          ) : null}
          <div className="customer-quote__fee-cards">
              {groups.map((group) => {
                const expanded = group.feeCategory !== "tax" && (expandedGroupKeys === null || expandedGroupKeys.has(group.key));
                const displayedGroup = summaryGroups.find((item) => item.key === group.key)!;
                const originalStudentUnit = snapshot.studentCount > 0
                  ? calculateCustomerQuoteRoleSummary({ ...snapshot, lines: snapshot.lines.filter((line) => group.lineNos.includes(line.lineNo)) }, "quoteAmount", true).studentUnitPrice
                  : null;
                const studentUnitDisplay = groupAmountState ?? (displayedGroup.studentUnitPrice === null
                  ? "—" : formatCustomerQuoteMoney(displayedGroup.studentUnitPrice, true));
                const showAmounts = amountDetailKeys.has(group.key);
                const groupSourceChangeCount = sourceChanges.filter((change) => (
                  change.sourceBudgetLineId !== null
                  && snapshot.lines.some((line) => (
                    group.lineNos.includes(line.lineNo)
                    && line.sourceBudgetLineId === change.sourceBudgetLineId
                  ))
                )).length;
                const isZeroGroup = compareCustomerQuoteMoney(group.costAmount) === 0
                  && compareCustomerQuoteMoney(group.quoteAmount) === 0;
                const expandedLines = expanded
                  ? snapshot.lines.filter((line) => group.lineNos.includes(line.lineNo))
                  : [];
                const pricingGroups = buildCustomerQuotePricingGroups(
                  expandedLines,
                  snapshot.pricingRulesVersion,
                );
                const groupedDisplayLines = pricingGroups.flatMap((pricingGroup) => pricingGroup.lines);
                const pricingGroupByLineNo = new Map(
                  pricingGroups.flatMap((pricingGroup) =>
                    pricingGroup.lineNos.map((lineNo) => [lineNo, pricingGroup] as const),
                  ),
                );
                const actualCostDisplayLocked = isCustomerQuoteActualCostDisplayLocked(
                  snapshot.pricingRulesVersion,
                  group.feeCategory,
                );
                const showStayAreaColumn = group.feeCategory === "stay";
                const showPointColumn = showStayAreaColumn || showsCustomerQuotePointColumn(group.feeCategory);
                const pointDisplayRuns = showPointColumn
                  ? showStayAreaColumn && groupSourceChangeCount > 0
                    ? new Map(groupedDisplayLines.flatMap((line) => [...buildCustomerQuotePointDisplayRuns([line], "stay")]))
                    : buildCustomerQuotePointDisplayRuns(groupedDisplayLines, showStayAreaColumn ? "stay" : "point")
                  : new Map<number, { rowSpan: number; label: string }>();
                const sourceChangeColSpan = group.feeCategory === "bus"
                  ? 5
                  : (showAmounts ? 6 : 4) + (showPointColumn ? 1 : 0);
                return <section key={group.key} id={`quote-fee-${group.key}`} className={`customer-quote__fee-card${selectedGroupKey === group.key ? " is-selected" : ""}${showAmounts ? " show-amounts" : ""}${group.feeCategory === "bus" ? " is-total-quote" : ""}`} aria-label={`${group.groupName}费用卡片`}>
                  <HorizontalScrollRegion className="customer-quote__fee-scroll" aria-label={`${group.groupName}费用明细表格，可横向滚动查看全部列`}>
                  <div className={`customer-quote__fee-heading ${expanded ? "is-expanded" : ""}${isZeroGroup ? " is-zero" : ""}${groupSourceChangeCount ? " has-source-change" : ""}`.trim()}>
                    <div className="customer-quote__fee-title"><button type="button" className="customer-quote__fee-toggle" aria-expanded={expanded} aria-label={expanded ? `收起${group.groupName}明细` : `查看${group.groupName} ${group.lineNos.length} 项明细`} disabled={group.feeCategory === "tax"} onClick={() => setExpandedGroupKeys((current) => { const next = new Set(current ?? groups.map((item) => item.key)); if (next.has(group.key)) next.delete(group.key); else next.add(group.key); return next; })}><span aria-hidden="true">{expanded ? "▾" : "▸"}</span><strong>{group.groupName}</strong></button>{group.feeCategory === "tax" ? null : <CustomerQuoteDisplayMenu
                      groupName={group.groupName}
                      disabled={
                        editingDisabled
                        || actualCostDisplayLocked
                      }
                      disabledReason={actualCostDisplayLocked
                        ? "当前规则要求实际费用独立展示，不可合并。"
                        : undefined}
                      value={group.displayMode}
                      onChange={(value) => changeGroupDisplayMode(group.lineNos, value)}
                    />}{group.financeTreatment === "agent_collection" ? <Tag color="orange">代收代付</Tag> : null}{groupSourceChangeCount ? <Tag color="red">{groupSourceChangeCount} 项待确认</Tag> : null}</div>
                    <div><span>核价成本</span><b>{formatCustomerQuoteMoney(group.costAmount, true)}</b></div>
                    <div className="customer-quote__original-quote"><span>取整前人均</span><b>{originalStudentUnit === null ? "—" : formatCustomerQuoteMoney(originalStudentUnit, true)}</b><small> /人/全程</small></div>
                    <div className="customer-quote-group-table__quote"><span>学生人均</span>{studentUnitDisplay}<small> /人/全程</small></div>
                    <div className="is-profit"><span>毛利</span>{groupAmountState ?? formatCustomerQuoteWholeYuan(displayedGroup.profitAmount)}</div>
                    <div><Button type="text" size="mini" aria-pressed={showAmounts} onClick={() => setAmountDetailKeys((current) => { const next = new Set(current); if (next.has(group.key)) next.delete(group.key); else next.add(group.key); return next; })}>金额明细</Button></div>
                  </div>
                  {expanded ? <div className="customer-quote__expanded-row">
                    <div className="customer-quote__expanded-detail">
                      <div className="customer-quote-table-wrap">
                        <table className="customer-quote-detail-table">
                          <thead><tr>{showPointColumn ? <th className="customer-quote-line-point">{showStayAreaColumn ? "区域" : "考察点"}</th> : null}<th className="customer-quote-line-name">费用名称 / 角色</th><th className="customer-quote__quantity-column">{customerQuoteDetailQuantityHeading(groupedDisplayLines, snapshot)}</th><th className="customer-quote__price-column">成本单价</th><th className="customer-quote__markup-column">{["big_transport", "ticket"].includes(group.feeCategory) ? "代收代付 / 加价" : "每单位加价"}</th><th className="customer-quote__cost-column">核价成本</th><th className="customer-quote__quote-column">报价金额（取整前）</th><th className="customer-quote__unit-column">学生人均</th></tr></thead>
                          <tbody>{groupedDisplayLines.map((line) => {
                            const pricingGroup = pricingGroupByLineNo.get(line.lineNo)
                              ?? buildCustomerQuotePricingGroups(
                                [line],
                                snapshot.pricingRulesVersion,
                              )[0];
                            const groupLineNo = pricingGroup.lineNos[0];
                            const markupField = `line-markup-${groupLineNo}`;
                            const amountField = `line-amount-${line.lineNo}`;
                            const isGroupFirstLine = line.lineNo === groupLineNo;
                            const isGroupLastLine = line.lineNo
                              === pricingGroup.lineNos[pricingGroup.lineNos.length - 1];
                            const pointRun = pointDisplayRuns.get(line.lineNo);
                            const busScope = customerQuoteBusScope(line);
                            const groupMarkup = customerQuotePricingGroupBaseMarkup(pricingGroup);
                            const pricingGroupDisplayModes = new Set(
                              pricingGroup.lines.map((item) => item.displayMode),
                            );
                            const pricingGroupDisplayMode: CustomerQuoteLine["displayMode"] | "mixed" =
                              pricingGroupDisplayModes.size === 1
                                ? [...pricingGroupDisplayModes][0]
                                : "mixed";
                            const sourceChange = sourceChanges.find((change) => (
                              change.changeType !== "added"
                              && change.changeType !== "header_modified"
                              && change.sourceBudgetLineId === line.sourceBudgetLineId
                            ));
                            const pricingGroupSourceChanges = isGroupLastLine
                              ? sourceChanges.filter((change) => (
                                change.changeType !== "added"
                                && change.changeType !== "header_modified"
                                && pricingGroup.lines.some((item) => (
                                  item.sourceBudgetLineId === change.sourceBudgetLineId
                                ))
                              ))
                              : [];
                            return <Fragment key={`${line.lineOrigin}:${line.sourceBudgetLineId ?? line.lineNo}`}>
                            <tr className={sourceChange ? `has-source-change is-${sourceChange.changeType}` : undefined}>
                              {showPointColumn && pointRun ? (
                                <td
                                  className="customer-quote-line-point customer-quote-line-point--merged"
                                  rowSpan={pointRun.rowSpan}
                                >
                                  {pointRun.label}
                                </td>
                              ) : null}
                              <td className="customer-quote-line-name" title={[line.customerDesc, line.resourceType].filter(Boolean).join(" · ")}>
                                <div className="customer-quote-line-name__heading">
                                  <span className={busScope ? "customer-quote-line-name__bus" : undefined}>
                                    <span>{pricingGroup.name} · {customerQuoteRoleLabel(line)}</span>
                                    {busScope ? <><span className="customer-quote-line-name__region">{busScope.region}</span><small>{busScope.scope}</small></> : null}
                                  </span>
                                  <CustomerQuoteDisplayMenu groupName={`${pricingGroup.name}小项第${line.lineNo}行`} value={pricingGroupDisplayMode}
                                    disabled={editingDisabled || actualCostDisplayLocked} disabledReason={actualCostDisplayLocked ? "当前规则要求实际费用独立展示，不可合并。" : undefined}
                                    onChange={(value) => changeGroupDisplayMode(pricingGroup.lineNos, value)} />
                                </div>
                              </td>
                              <td className="customer-quote__quantity-column">{formatCustomerQuoteLineQuantity(line, snapshot)}</td>
                              <td>{formatCustomerQuoteMoney(line.costUnitPrice, true)}</td>
                              {isGroupFirstLine ? <td
                                className="customer-quote-line-markup--merged customer-quote__markup-column"
                                rowSpan={pricingGroup.lines.length}
                              >
                              {pricingGroup.lines.every(canToggleCustomerQuoteAgentCollection) ? (
                                <div className="customer-quote-agent-collection-control">
                                  <span>代收代付</span>
                                  <Switch
                                    size="small"
                                    aria-label={`${pricingGroup.name}代收代付`}
                                    checked={pricingGroup.lines.every((item) => (
                                      item.financeTreatment === "agent_collection"
                                    ))}
                                    disabled={editingDisabled}
                                    onChange={(checked) => (
                                      runtime.setLinesAgentCollection(pricingGroup.lineNos, checked)
                                    )}
                                  />
                                  <strong>{pricingGroup.lines.every((item) => (
                                    item.financeTreatment === "agent_collection"
                                  )) ? "是" : "否"}</strong>
                                  <small>开启代收后加价归零，按核价成本报价</small>
                                </div>
                                ) : null}
                                {line.financeTreatment === "agent_collection" ? (
                                <div className="customer-quote__total-adjustment">
                                  <Tag color="gray">加价 {formatCustomerQuoteMoney(customerQuoteUnitMarkup(line), true)}</Tag>
                                  <small>{compareCustomerQuoteMoney(customerQuoteUnitMarkup(line)) === 0 ? "代收代付不允许加价" : "历史报价金额，只读保留"}</small>
                                </div>
                              ) : line.feeCategory === "bus" ? (
                                <div className="customer-quote__total-adjustment">
                                  <Tag color="arcoblue">整条总额调整</Tag>
                                  <small>按车辆与行程整体议价</small>
                                </div>
                              ) : line.feeCategory === "tax"
                                && compareCustomerQuoteMoney(line.quoteAmount, line.costAmount) !== 0 ? (
                                <div className="customer-quote__total-adjustment">
                                  <Tag color="orange">沿用原报价</Tag>
                                  <small>核价变化后保持已确认金额</small>
                                </div>
                              ) : line.profitPolicy === "none" ? (
                                <div className="customer-quote__total-adjustment">
                                  <Tag color="gray">不加价</Tag>
                                  <small>按核价成本报价</small>
                                </div>
                              ) : (
                                <>
                                  <InputNumber
                                    key={`${customerQuoteId}:${lineMarkupScopeRevision}:${groupLineNo}:${lineMarkupRevisions[groupLineNo] ?? 0}`}
                                    className="customer-quote-markup-input"
                                    hideControl
                                    aria-label={customerQuoteMarkupAriaLabel(pricingGroup, line)}
                                    aria-invalid={Boolean(fieldErrors[markupField])}
                                    aria-describedby={fieldErrors[markupField]
                                      ? customerQuoteFieldErrorId(markupField)
                                      : undefined}
                                    inputMode="decimal"
                                    placeholder="0"
                                    prefix="¥"
                                    step={1}
                                    precision={2}
                                    formatter={(value, { userTyping, input }) => userTyping ? input : formatCustomerQuoteCompactDecimal(value)}
                                    strictMode
                                    value={lineMarkupDrafts[groupLineNo] ?? groupMarkup}
                                    error={Boolean(fieldErrors[markupField])}
                                    disabled={editingDisabled || pricingGroup.lines.every((item) => isZeroQuantity(item.quantity))}
                                    onFocus={() => {
                                      const currentMarkup = customerQuotePricingGroupBaseMarkup(pricingGroup);
                                      focusedMarkupLineRef.current = groupLineNo;
                                      lineMarkupInitialValuesRef.current.set(groupLineNo, currentMarkup);
                                      lineMarkupLastCommittedRef.current.set(groupLineNo, currentMarkup);
                                      setLineMarkupDrafts((current) => ({
                                        ...current,
                                        [groupLineNo]: currentMarkup,
                                      }));
                                    }}
                                    onInput={(event) => {
                                      const invalid = normalizePricingGroupMarkup(
                                        pricingGroup,
                                        event.currentTarget.value,
                                      ) === null;
                                      rawInputInvalidRef.current[markupField] = invalid;
                                      updateFieldError(
                                        markupField,
                                        invalid ? markupInputErrorMessage : null,
                                      );
                                    }}
                                    onChange={(value: number | string) => {
                                      const draft = typeof value === "string" ? value : String(value ?? "");
                                      setLineMarkupDrafts((current) => ({ ...current, [groupLineNo]: draft }));
                                      const normalized = normalizePricingGroupMarkup(pricingGroup, draft);
                                      const invalid = rawInputInvalidRef.current[markupField]
                                        || normalized === null;
                                      updateFieldError(
                                        markupField,
                                        invalid ? markupInputErrorMessage : null,
                                      );
                                      if (
                                        !invalid
                                        && normalized !== null
                                        && lineMarkupLastCommittedRef.current.get(groupLineNo) !== normalized
                                      ) {
                                        lineMarkupLastCommittedRef.current.set(groupLineNo, normalized);
                                        commitPricingGroupMarkup(pricingGroup, normalized);
                                      }
                                    }}
                                    onBlur={() => {
                                      finishPricingGroupMarkupEdit(pricingGroup);
                                      focusedMarkupLineRef.current = null;
                                    }}
                                    onKeyDownCapture={(event) => {
                                      if (event.key !== "Escape" && event.key !== "Esc") return;
                                      event.preventDefault();
                                      event.stopPropagation();
                                      cancelPricingGroupMarkupEdit(pricingGroup);
                                    }}
                                    onKeyDown={(event) => {
                                      const key = "key" in event ? String(event.key) : "";
                                      if (key === "Enter") {
                                        event.preventDefault();
                                        finishPricingGroupMarkupEdit(pricingGroup);
                                        if (event.currentTarget instanceof HTMLElement) event.currentTarget.blur();
                                      }
                                    }}
                                  />
                                  <CustomerQuoteFieldError
                                    field={markupField}
                                    message={fieldErrors[markupField]}
                                  />
                                </>
                              )}</td> : null}
                              <td className="customer-quote-line-source customer-quote__cost-column"><strong>{formatCustomerQuoteMoney(line.costAmount, true)}</strong></td>
                              <td className="customer-quote__quote-column">{line.feeCategory === "bus" ? (
                                <div className="customer-quote__amount-editor">
                                  <InputNumber
                                    key={`${customerQuoteId}:${line.lineNo}:${lineAmountRevisions[line.lineNo] ?? 0}`}
                                    className="customer-quote-amount-input"
                                    hideControl
                                    aria-label={`${line.customerName}报价金额`}
                                    aria-invalid={Boolean(fieldErrors[amountField])}
                                    aria-describedby={joinAriaDescriptions(
                                      `customer-quote-bus-adjustment-${line.lineNo}`,
                                      fieldErrors[amountField]
                                        && customerQuoteFieldErrorId(amountField),
                                    )}
                                    inputMode="decimal"
                                    placeholder="0"
                                    prefix="¥"
                                    aria-valuemin={line.lineOrigin === "budget" ? 0 : undefined}
                                    step={1}
                                    precision={2}
                                    formatter={(value, { userTyping, input }) => userTyping ? input : formatCustomerQuoteCompactDecimal(value)}
                                    value={lineAmountDrafts[line.lineNo] ?? line.quoteAmount}
                                    error={Boolean(fieldErrors[amountField])}
                                    disabled={editingDisabled}
                                    onFocus={() => {
                                      focusedAmountLineRef.current = line.lineNo;
                                      lineAmountInitialValuesRef.current.set(line.lineNo, line.quoteAmount);
                                      lineAmountLastCommittedRef.current.set(
                                        line.lineNo,
                                        normalizeQuoteAmount(line.quoteAmount, line.lineOrigin === "budget" ? 0 : undefined)
                                          ?? line.quoteAmount,
                                      );
                                      setLineAmountDrafts((current) => ({
                                        ...current,
                                        [line.lineNo]: line.quoteAmount,
                                      }));
                                    }}
                                    onInput={(event) => handleRawAmountInput(
                                      amountField,
                                      event.currentTarget.value,
                                      line.lineOrigin === "budget" ? 0 : undefined,
                                    )}
                                    onChange={(value) => {
                                      const draft = String(value ?? "");
                                      const min = line.lineOrigin === "budget" ? 0 : undefined;
                                      setLineAmountDrafts((current) => ({ ...current, [line.lineNo]: draft }));
                                      const normalized = normalizeQuoteAmount(draft, min);
                                      const invalid = rawInputInvalidRef.current[amountField]
                                        || normalized === null;
                                      updateFieldError(
                                        amountField,
                                        invalid ? moneyInputErrorMessage : null,
                                      );
                                      if (
                                        !invalid
                                        && normalized !== null
                                        && lineAmountLastCommittedRef.current.get(line.lineNo) !== normalized
                                      ) {
                                        lineAmountLastCommittedRef.current.set(line.lineNo, normalized);
                                        updateLine(line, { quoteAmount: normalized });
                                      }
                                    }}
                                    onBlur={() => {
                                      finishLineAmountEdit(
                                        line,
                                        line.lineOrigin === "budget" ? 0 : undefined,
                                      );
                                      focusedAmountLineRef.current = null;
                                    }}
                                    onKeyDownCapture={(event) => {
                                      if (event.key !== "Escape" && event.key !== "Esc") return;
                                      event.preventDefault();
                                      event.stopPropagation();
                                      cancelLineAmountEdit(line);
                                    }}
                                    onKeyDown={(event) => {
                                      const key = "key" in event ? String(event.key) : "";
                                      if (key === "Enter") {
                                        event.preventDefault();
                                        finishLineAmountEdit(
                                          line,
                                          line.lineOrigin === "budget" ? 0 : undefined,
                                        );
                                        if (event.currentTarget instanceof HTMLElement) event.currentTarget.blur();
                                      }
                                    }}
                                  />
                                  <CustomerQuoteFieldError
                                    field={amountField}
                                    message={fieldErrors[amountField]}
                                  />
                                  <div
                                    id={`customer-quote-bus-adjustment-${line.lineNo}`}
                                    className="customer-quote__amount-feedback"
                                    role="status"
                                    aria-live="polite"
                                  >
                                    <small>{customerQuoteMarkupRatePercent(line) === null
                                      ? "加价 —（核价成本为 ¥0）"
                                      : `加价 ${customerQuoteMarkupRatePercent(line)}`}</small>
                                    {exceedsCustomerQuoteBusAutoMarkupCap(line) ? (
                                      <small className="customer-quote__manual-price-warning">已超过自动加价 30% 上限，允许手工报价但请确认议价依据。</small>
                                    ) : null}
                                  </div>
                                </div>
                              ) : <output className="customer-quote__calculated-amount" aria-label={`${line.customerName}报价金额`}>
                                <strong>{formatCustomerQuoteMoney(line.quoteAmount, true)}</strong>
                                {line.feeCategory !== "tax" ? (
                                  <small>{calculatedAmountFormula(line, customerQuoteUnitMarkup(line))}</small>
                                ) : null}
                              </output>}</td>
                              {line === groupedDisplayLines[0] ? <td className="customer-quote__unit-cell" rowSpan={groupedDisplayLines.length + pricingGroups.filter((item) => sourceChanges.some((change) => change.changeType !== "added" && change.changeType !== "header_modified" && item.lines.some((row) => row.sourceBudgetLineId === change.sourceBudgetLineId))).length}>
                                <strong>{studentUnitDisplay}</strong><span> /人/全程</span>
                              </td> : null}
                            </tr>
                            {pricingGroupSourceChanges.length ? (
                              <tr className="customer-quote__source-change-row">
                                <td colSpan={sourceChangeColSpan}>
                                  <div className="customer-quote__source-change-list">
                                    {pricingGroupSourceChanges.map((change) => (
                                      <SourceChangeWarning
                                        key={change.changeId}
                                        change={change}
                                        confirming={runtime.confirmingSourceChange}
                                        canConfirm={canConfirmSourceChange}
                                        blockedReason={writeActionBlockedReason}
                                        isDirty={runtime.isDirty}
                                        onConfirm={(...args) => runtime.confirmSourceChange(...args).catch((error: unknown) => { showActionFailure(error); throw error; })}
                                      />
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            ) : null}
                            </Fragment>;
                          })}</tbody>
                        </table>
                      </div>
                      <div className="customer-quote__fee-footer"><span>{customerQuoteFeeCategoryRuleLabel(group.feeCategory, snapshot.pricingRulesVersion)} {useRoundedSummary ? "人均、毛利使用标准取整结果；明细报价为取整前编辑金额。" : "当前为取整前金额。"}</span><strong>学生 {snapshot.studentCount} 人 · {studentUnitDisplay} /人/全程{snapshot.sourceTeacherPays ? ` · 老师 ${groupAmountState ?? formatCustomerQuoteMoney(displayedGroup.teacherUnitPrice, true)} /人/全程` : "（含老师分摊）"}</strong></div>
                    </div>
                  </div> : null}
                  </HorizontalScrollRegion>
                </section>;
              })}
          </div>
        </section>


        <section className="customer-quote__remark"><label>内部备注<Input.TextArea aria-label="内部备注" value={runtime.remark} disabled={editingDisabled} autoSize={{ minRows: 2, maxRows: 4 }} onChange={runtime.setRemark} /></label></section>

        {salesSummary ? <CustomerQuoteAdjustmentDetails records={feeAdjustmentRecords} /> : null}
        </div>
      </div>
      {showBackToTop ? <div className="customer-quote__compact-actions" aria-label="报价快捷操作">
        {isDraft && canUpdate ? <Button type="primary" disabled={saveDisabled} loading={runtime.saving} onClick={() => void runtime.save().then(() => Message.success(`报价 V${editor.versionNo} 草稿已保存`)).catch(showActionFailure)}>保存草稿（快捷）</Button> : null}
        <Button onClick={backToPageTop}>返回报价页头</Button>
      </div> : null}
      {showBackToTop ? <Button
        className="customer-quote__back-to-top"
        type="primary"
        shape="circle"
        icon={<ArrowUp size={18} />}
        aria-label="回到顶部"
        title="回到顶部"
        onClick={backToPageTop}
      /> : null}
      </div>
    </>
  );
}
